<template>
  <div class="py-4 container-fluid timereg-container">
    <div class="row">
      <div class="col-12">
        <card :title="$t('timeRegistration.title')">
          <!-- ── Top-level tabs (Stimpla / Yfirlit) ─────────── -->
          <template #table-select>
            <div class="btn-group tab-switch screen-only" role="group">
              <button class="btn btn-sm" :class="activeTab === 0 ? 'btn-dark' : 'btn-outline-secondary'" @click="switchTab(0)">
                <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">{{ endOfDayEnabled ? 'edit_calendar' : 'schedule' }}</i>
                {{ endOfDayEnabled ? $t('endOfDay.registerDayTab') : $t('timeRegistration.clockTab') }}
              </button>
              <button class="btn btn-sm" :class="activeTab === 1 ? 'btn-dark' : 'btn-outline-secondary'" @click="switchTab(1)">
                <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">list_alt</i>
                {{ $t('timeRegistration.overviewTab') }}
              </button>
            </div>
          </template>

          <!-- ══════════ TAB 0: Clock Widget ══════════════════ -->
          <div v-show="activeTab === 0">
            <!-- End-of-day registration (EnableEndOfDayTimeRegistration): Skrá dag instead of the clock -->
            <div v-if="endOfDayEnabled" class="row justify-content-center">
              <div class="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
                <day-registration-tab ref="dayTab" :key="dayTabKey" :current-user-id="currentUserId" :phases-enabled="phasesEnabled" :reference-enabled="referenceEnabled" :absence-types="absenceTypes" />
              </div>
            </div>
            <template v-else>
            <p class="text-center text-secondary text-sm mb-3">{{ formattedDate }}</p>
            <div class="row justify-content-center">
              <div class="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
                <div class="timeclock-clock-card mb-3">
                  <div class="timeclock-clock-icon"><i class="material-icons">schedule</i></div>
                  <div class="timeclock-time">{{ currentTime }}</div>
                </div>

                <div v-if="activeEntry" class="card p-3 mb-3">
                  <template v-if="activeEntry.isAbsence">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <i class="material-icons text-danger">event_busy</i>
                      <span class="fw-semibold">{{ absenceLabel(activeEntry.timesheetEntryType) }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <i class="material-icons text-secondary" style="font-size:1rem">access_time</i>
                      <span class="fw-bold text-success" style="font-size:1.25rem">{{ absenceHoursLabel(activeEntry) }}</span>
                      <span class="text-secondary text-sm">{{ $t('timeRegistration.hoursRegistered') }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <i class="material-icons text-secondary">{{ activeEntry.timeRegistrationGroupId ? 'groups' : 'work' }}</i>
                      <span class="fw-bold flex-grow-1">
                        <template v-if="activeEntry.timeRegistrationGroupId">
                          <span class="text-secondary me-1">{{ $t('timeRegistration.groupLabel') }}:</span>{{ activeEntry.timeRegistrationGroupName }}
                        </template>
                        <template v-else>
                          <span v-if="activeEntry.projectKey" class="text-secondary me-1">[{{ activeEntry.projectKey }}]</span>{{ activeEntry.projectName }}<span v-if="activeEntry.projectPhaseName" class="text-secondary"> — {{ activeEntry.projectPhaseName }}</span>
                        </template>
                      </span>
                      <span class="fw-bold text-warning" style="font-size:1.15rem">{{ elapsedTime }}</span>
                    </div>
                    <p class="text-secondary text-xs mb-1 ms-4 d-flex align-items-center gap-1">
                      {{ $t('timeRegistration.clockedIn') }}: {{ formatTime(activeEntry.realClockIn) }}
                      <button v-if="canEditTodayEntry(activeEntry)" class="btn-icon text-secondary" :title="$t('timeRegistration.editEntry')" @click="openEntryEdit(activeEntry)"><i class="material-icons">edit</i></button>
                    </p>
                    <p v-if="onBreak" class="text-warning text-xs fw-semibold mb-1 ms-4">
                      <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">free_breakfast</i>
                      {{ $t('timeRegistration.onBreakSince') }}: {{ formatTime(activeEntry.openBreakStart) }}
                    </p>
                    <p v-if="activeEntry.breakMinutes > 0" class="text-secondary text-xs mb-1 ms-4">{{ $t('timeRegistration.breakMinutesToday') }}: {{ activeEntry.breakMinutes }} {{ $t('timeRegistration.minutesShort') }}</p>
                    <div class="d-flex gap-2 mt-2">
                      <button v-if="!activeEntry.timeRegistrationGroupId" class="btn btn-outline-secondary flex-grow-1" :disabled="loading" @click="openProjectModal('switch')">
                        <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">swap_horiz</i> {{ $t('timeRegistration.switchProject') }}
                      </button>
                      <button class="btn btn-danger flex-grow-1" :disabled="loading" @click="clockOut">
                        <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">logout</i> {{ $t('timeRegistration.clockOut') }}
                      </button>
                    </div>
                    <button
                      v-if="breakButtonEnabled && !activeEntry.timeRegistrationGroupId"
                      class="btn w-100 mt-2"
                      :class="onBreak ? 'btn-warning' : 'btn-outline-warning'"
                      :disabled="loading"
                      @click="toggleBreak"
                    >
                      <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">{{ onBreak ? 'play_arrow' : 'free_breakfast' }}</i>
                      {{ onBreak ? $t('timeRegistration.endBreak') : $t('timeRegistration.startBreak') }}
                    </button>
                  </template>
                </div>

                <div v-else class="d-flex flex-column gap-2">
                  <div v-if="simplifiedEnabled && myGroup" class="text-center text-secondary text-sm">
                    <i class="material-icons" style="font-size:1rem;vertical-align:middle">groups</i>
                    {{ $t('timeRegistration.groupLabel') }}: <strong>{{ myGroup.name }}</strong>
                  </div>
                  <div v-else-if="simplifiedEnabled && !myGroup" class="alert alert-warning text-center text-sm mb-0 py-2">
                    {{ $t('timeRegistration.noGroupAssigned') }}
                  </div>
                  <button class="btn btn-dark w-100" :disabled="loading || (simplifiedEnabled && !myGroup)" @click="startClockIn">
                    <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">login</i> {{ $t('timeRegistration.clockIn') }}
                  </button>
                  <button class="btn btn-outline-secondary w-100" :disabled="loading" @click="openAbsenceModal">
                    <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">event_busy</i> {{ $t('timeRegistration.registerAbsence') }}
                  </button>
                </div>

                <!-- My hours this salary period -->
                <div v-if="periodSummary.loaded" class="card p-3 mt-3">
                  <div class="d-flex align-items-center gap-2">
                    <i class="material-icons text-secondary" style="font-size:1.1rem">event_note</i>
                    <span class="text-sm text-secondary flex-grow-1">{{ $t('timeRegistration.myPeriodHours') }}<br /><span class="text-xs">{{ periodSummary.label }}</span></span>
                    <span class="fw-bold" style="font-size:1.15rem">{{ periodSummary.hours }} {{ $t('timeRegistration.hrs') }}</span>
                  </div>
                </div>

                <div v-if="completedTodayEntries.length > 0" class="mt-4">
                  <p class="text-secondary text-xs mb-2 text-uppercase fw-semibold" style="letter-spacing:0.05em">{{ $t('timeRegistration.todayFilter') }}</p>
                  <div v-for="entry in completedTodayEntries" :key="entry.id" class="d-flex align-items-center gap-2 py-2 px-3 today-entry-row rounded mb-1">
                    <i class="material-icons text-secondary" style="font-size:1rem">{{ entry.isAbsence ? 'event_busy' : (entry.timeRegistrationGroupId ? 'groups' : 'work_outline') }}</i>
                    <div class="flex-grow-1" style="min-width:0">
                      <div class="text-sm text-truncate">
                        <template v-if="entry.isAbsence">{{ absenceLabel(entry.timesheetEntryType) }}</template>
                        <template v-else-if="entry.timeRegistrationGroupId">{{ entry.timeRegistrationGroupName }}</template>
                        <template v-else>{{ (entry.projectKey ? '[' + entry.projectKey + '] ' : '') + entry.projectName + (entry.projectPhaseName ? ' — ' + entry.projectPhaseName : '') }}</template>
                      </div>
                      <div v-if="canEditTodayEntry(entry)" class="text-xs text-secondary">{{ formatTime(entry.realClockIn) }}–{{ formatTime(entry.realClockOut) }}</div>
                    </div>
                    <span class="text-sm fw-semibold ms-2 text-nowrap">
                      <template v-if="entry.isAbsence">{{ absenceHoursLabel(entry) }}</template>
                      <template v-else-if="entry.timeRegistrationGroupId">{{ formatHoursBreakdown(entry) }}</template>
                      <template v-else>{{ minutesToHM(realEntryMinutes(entry)) }}</template>
                    </span>
                    <button v-if="canEditTodayEntry(entry)" class="btn-icon text-secondary today-entry-edit" :title="$t('timeRegistration.editEntry')" @click="openEntryEdit(entry)"><i class="material-icons">edit</i></button>
                  </div>
                </div>
              </div>
            </div>
            </template>
          </div>

          <!-- ══════════ TAB 1: Overview ══════════════════════ -->
          <div v-show="activeTab === 1" class="screen-only">

            <!-- ── Overview header ─────────────────────────── -->
            <div class="overview-header" :class="currentView === 'personal' ? 'oh-personal' : 'oh-project'">
              <div class="oh-top">
                <div class="oh-titles">
                  <p class="oh-eyebrow mb-0">{{ dateRangeLabel }}</p>
                  <h5 class="oh-title mb-0">{{ currentView === 'project' ? $t('timeRegistration.projectView') : currentView === 'absence' ? $t('timeRegistration.absenceView') : $t('timeRegistration.overviewAndChanges') }}</h5>
                </div>
                <!-- View switcher: 3 buttons when the absence view is enabled (admins), otherwise the classic 2-way toggle. Hidden in simplified mode — no project concept -->
                <div v-if="showAbsenceView" class="oh-switch">
                  <button class="btn btn-sm mb-0" :class="currentView === 'personal' ? 'btn-light' : 'btn-outline-light'" @click="setView('personal')">
                    <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">person</i>{{ $t('timeRegistration.personalView') }}
                  </button>
                  <button class="btn btn-sm mb-0" :class="currentView === 'project' ? 'btn-light' : 'btn-outline-light'" @click="setView('project')">
                    <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">folder</i>{{ $t('timeRegistration.projectView') }}
                  </button>
                  <button class="btn btn-sm mb-0" :class="currentView === 'absence' ? 'btn-light' : 'btn-outline-light'" @click="setView('absence')">
                    <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">event_busy</i>{{ $t('timeRegistration.absenceView') }}
                  </button>
                </div>
                <button
                  v-else-if="!simplifiedEnabled"
                  class="oh-toggle"
                  :title="currentView === 'project' ? $t('timeRegistration.personalView') : $t('timeRegistration.projectView')"
                  @click="toggleView"
                >
                  <i class="material-icons">{{ currentView === 'project' ? 'person' : 'folder' }}</i>
                  <span class="oh-toggle-label">{{ currentView === 'project' ? $t('timeRegistration.personalView') : $t('timeRegistration.projectView') }}</span>
                </button>
              </div>

              <!-- Scope pickers: user (personal view, admin) / project (project view, classic mode) -->
              <select
                v-if="isAdmin && currentView === 'personal' && users.length"
                v-model="selectedUserId"
                class="form-select form-select-sm header-select"
                @change="fetchEntries"
              >
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}{{ u.id === currentUserId ? ` (${$t('timeRegistration.you')})` : '' }}</option>
              </select>
              <select v-if="!simplifiedEnabled && currentView === 'project'" v-model="projectViewProjectId" class="form-select form-select-sm header-select">
                <option v-for="p in effectiveProjectNames" :key="p" :value="p">{{ p }}</option>
              </select>

              <!-- Stats (classic mode): work hours + overtime from clock durations -->
              <div v-if="!simplifiedEnabled && currentView !== 'absence' && hasOverviewData" class="oh-stats">
                <div class="oh-stat">
                  <div class="oh-stat-label">
                    <i class="material-icons">schedule</i><span>{{ $t('timeRegistration.workHours') }}</span>
                  </div>
                  <div class="oh-stat-value">
                    {{ minutesToDecimal(isAdmin && adjustmentsEnabled ? currentStats.adjNormal : currentStats.realNormal) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span>
                  </div>
                  <div v-if="isAdmin && adjustmentsEnabled" class="oh-stat-sub">
                    {{ $t('timeRegistration.realTime') }} <strong>{{ minutesToDecimal(currentStats.realNormal) }}</strong> {{ $t('timeRegistration.hrs') }}
                  </div>
                </div>
                <div class="oh-stat">
                  <div class="oh-stat-label">
                    <i class="material-icons">trending_up</i><span>{{ $t('timeRegistration.overtimeHours') }}</span>
                  </div>
                  <div class="oh-stat-value">
                    {{ minutesToDecimal(isAdmin && adjustmentsEnabled ? currentStats.adjOT : currentStats.realOT) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span>
                  </div>
                  <div v-if="isAdmin && adjustmentsEnabled" class="oh-stat-sub">
                    {{ $t('timeRegistration.realTime') }} <strong>{{ minutesToDecimal(currentStats.realOT) }}</strong> {{ $t('timeRegistration.hrs') }}
                  </div>
                </div>
              </div>

              <!-- Stats (simplified mode): Vinna / Eftirvinna / Yfirvinna / Stórhátíðartímar from user-entered hours -->
              <div v-if="simplifiedEnabled && hasOverviewData" class="oh-stats oh-stats-4">
                <div class="oh-stat">
                  <div class="oh-stat-label"><i class="material-icons">schedule</i><span>{{ $t('timeRegistration.normalHours') }}</span></div>
                  <div class="oh-stat-value">{{ formatDecimal(simplifiedPeriodTotals().normal) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span></div>
                </div>
                <div class="oh-stat">
                  <div class="oh-stat-label"><i class="material-icons">more_time</i><span>{{ $t('timeRegistration.afterHours') }}</span></div>
                  <div class="oh-stat-value">{{ formatDecimal(simplifiedPeriodTotals().after) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span></div>
                </div>
                <div class="oh-stat">
                  <div class="oh-stat-label"><i class="material-icons">trending_up</i><span>{{ $t('timeRegistration.overtimeHours') }}</span></div>
                  <div class="oh-stat-value">{{ formatDecimal(simplifiedPeriodTotals().overtime) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span></div>
                </div>
                <div class="oh-stat">
                  <div class="oh-stat-label"><i class="material-icons">celebration</i><span>{{ $t('timeRegistration.redDayHours') }}</span></div>
                  <div class="oh-stat-value">{{ formatDecimal(simplifiedPeriodTotals().redDay) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span></div>
                </div>
                <div class="oh-total">
                  <span class="oh-total-label">{{ $t('timeRegistration.totalHours') }}</span>
                  <span class="oh-total-value">{{ formatDecimal(simplifiedPeriodTotals().total) }}<span class="oh-unit">{{ $t('timeRegistration.hrs') }}</span></span>
                </div>
              </div>
            </div>

            <!-- ── Date range filters ──────────────────────── -->
            <div class="range-filters d-flex flex-wrap align-items-start gap-2 my-3">
              <!-- Project view uses the work period (Verktímabil — full calendar month); personal view uses the salary period -->
              <div class="range-filter-item d-flex flex-column align-items-center">
                <button class="btn btn-sm mb-0" :class="activeRange === currentPeriodRange ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange(currentPeriodRange)">
                  <span class="d-none d-md-inline">{{ currentView === 'project' ? $t('timeRegistration.currentWorkPeriodFilter') : $t('timeRegistration.currentSalaryPeriodFilter') }}</span>
                  <span class="d-md-none">{{ currentView === 'project' ? $t('timeRegistration.currentWorkPeriodFilterShort') : $t('timeRegistration.currentSalaryPeriodFilterShort') }}</span>
                </button>
                <small class="text-secondary text-xs mt-1">{{ currentView === 'project' ? currentWorkPeriodLabel : currentSalaryPeriodLabel }}</small>
              </div>
              <div class="range-filter-item d-flex flex-column align-items-center">
                <button class="btn btn-sm mb-0" :class="activeRange === lastPeriodRange ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange(lastPeriodRange)">
                  <span class="d-none d-md-inline">{{ currentView === 'project' ? $t('timeRegistration.lastWorkPeriodFilter') : $t('timeRegistration.lastSalaryPeriodFilter') }}</span>
                  <span class="d-md-none">{{ currentView === 'project' ? $t('timeRegistration.lastWorkPeriodFilterShort') : $t('timeRegistration.lastSalaryPeriodFilterShort') }}</span>
                </button>
                <small class="text-secondary text-xs mt-1">{{ currentView === 'project' ? lastWorkPeriodLabel : lastSalaryPeriodLabel }}</small>
              </div>
              <div class="range-filter-item d-flex flex-column align-items-center">
                <button class="btn btn-sm mb-0" :class="showDatePicker ? 'btn-dark' : 'btn-outline-secondary'" @click="showDatePicker = !showDatePicker">
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">date_range</i> {{ $t('timeRegistration.chooseDates') }}
                </button>
              </div>
              <div class="range-filter-item d-flex flex-column align-items-center">
                <button class="btn btn-sm mb-0" :class="activeRange === 'week'  ? 'btn-dark' : 'btn-outline-secondary'" @click="setRange('week')">{{ $t('timeRegistration.weekFilter') }}</button>
                <small class="text-secondary text-xs mt-1">{{ weekRangeLabel }}</small>
              </div>
              <div v-if="(currentView === 'project' && isAdmin) || simplifiedEnabled" class="range-filter-actions d-flex align-items-center gap-2 ms-auto">
                <div v-if="!simplifiedEnabled" class="form-check mb-0">
                  <input id="includeOvertimeReport" v-model="includeOvertimeInReport" class="form-check-input" type="checkbox" />
                  <label class="form-check-label text-sm" for="includeOvertimeReport">{{ $t('timeRegistration.includeOvertime') }}</label>
                </div>
                <button class="btn btn-sm btn-outline-dark" @click="printReport">
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">description</i> {{ $t('timeRegistration.createReport') }}
                </button>
                <button v-if="currentView === 'project' && isAdmin && erpTimeSyncEnabled" class="btn btn-sm btn-outline-dark" :disabled="erpSyncing" @click="syncToErp">
                  <span v-if="erpSyncing" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="material-icons me-1" style="font-size:1rem;vertical-align:middle">sync</i>
                  {{ erpSyncing ? $t('timeRegistration.syncToErpInProgress') : $t('timeRegistration.syncToErp', { erp: erpDisplayName }) }}
                </button>
                <button v-if="simplifiedEnabled && isAdmin" class="btn btn-sm btn-outline-dark" :disabled="allUsersReportLoading" @click="printAllUsersReport">
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">groups</i> {{ $t('timeRegistration.allUsersReport') }}
                </button>
              </div>
              <div v-if="currentView === 'absence' && isAdmin && absenceSyncEnabled" class="range-filter-actions d-flex align-items-center gap-2 ms-auto">
                <button class="btn btn-sm btn-outline-dark" :disabled="erpSyncing" @click="syncToErp">
                  <span v-if="erpSyncing" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="material-icons me-1" style="font-size:1rem;vertical-align:middle">sync</i>
                  {{ erpSyncing ? $t('timeRegistration.syncToErpInProgress') : $t('timeRegistration.syncToErp', { erp: erpDisplayName }) }}
                </button>
              </div>
              <button
                v-if="isAdmin && excelExportEnabled"
                class="btn btn-sm btn-outline-success range-filter-excel"
                :class="{ 'ms-auto': !((currentView === 'project' && isAdmin) || simplifiedEnabled || (currentView === 'absence' && isAdmin && absenceSyncEnabled)) }"
                :disabled="excelExporting"
                @click="exportExcel"
              >
                <span v-if="excelExporting" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="material-icons me-1" style="font-size:1rem;vertical-align:middle">download</i>
                {{ $t('timeRegistration.exportExcel') }}
              </button>
            </div>
            <div v-if="showDatePicker" class="custom-date-row d-flex align-items-center gap-2 mb-3">
              <input v-model="customDateFrom" type="date" class="form-control form-control-sm custom-date-input" />
              <span class="text-secondary">–</span>
              <input v-model="customDateTo" type="date" class="form-control form-control-sm custom-date-input" />
              <button class="btn btn-sm btn-dark mb-0" @click="setRange('custom')">{{ $t('timeRegistration.fetchData') }}</button>
            </div>

            <!-- ── Salary period submission / approval ────────── -->
            <div v-if="isSalaryPeriodRange && periodApproval" class="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 rounded border">
              <i class="material-icons text-secondary" style="font-size:1.1rem">fact_check</i>
              <span class="text-sm fw-semibold">{{ $t('timeRegistration.periodStatusLabel') }}:</span>
              <span
                class="badge"
                :class="periodApproval.status === 2 ? 'bg-success' : (periodApproval.status === 1 ? 'bg-warning text-dark' : 'bg-secondary')"
              >
                {{ periodApproval.status === 2 ? $t('timeRegistration.periodApproved') : (periodApproval.status === 1 ? $t('timeRegistration.periodSubmitted') : $t('timeRegistration.periodOpen')) }}
              </span>
              <span v-if="periodApproval.status === 2 && periodApproval.approvedByUserName" class="text-xs text-secondary">
                {{ $t('timeRegistration.approvedBy') }} {{ periodApproval.approvedByUserName }}
              </span>
              <div class="period-bar-actions ms-auto d-flex gap-2">
                <button
                  v-if="!isAdmin && periodApproval.status == null && selectedUserId === currentUserId"
                  class="btn btn-sm btn-dark"
                  :disabled="periodActionLoading"
                  @click="submitPeriod"
                >
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">task_alt</i> {{ $t('timeRegistration.submitPeriod') }}
                </button>
                <button
                  v-if="isAdmin && periodApproval.status !== 2"
                  class="btn btn-sm btn-success"
                  :disabled="periodActionLoading"
                  @click="approvePeriod"
                >
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">check_circle</i> {{ $t('timeRegistration.approvePeriod') }}
                </button>
                <button
                  v-if="isAdmin && periodApproval.status != null"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="periodActionLoading"
                  @click="reopenPeriod"
                >
                  <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">lock_open</i> {{ $t('timeRegistration.reopenPeriod') }}
                </button>
              </div>
            </div>

            <!-- ── Manual entry (personal view) ────────────── -->
            <div v-if="currentView === 'personal'" class="mb-3">
              <button v-if="endOfDayEnabled && isAdmin" class="btn btn-sm btn-outline-dark me-2" @click="openDayEditor(null, selectedUserId)">
                <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">edit_calendar</i>{{ $t('endOfDay.registerDayTab') }}
              </button>
              <button class="btn btn-sm" :class="showManualForm ? 'btn-dark' : 'btn-outline-dark'" @click="toggleManualForm">
                <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">{{ showManualForm ? 'close' : 'add' }}</i>
                {{ showManualForm ? $t('timeRegistration.closeForm') : (endOfDayEnabled ? $t('timeRegistration.registerAbsence') : $t('timeRegistration.registerWorkDay')) }}
              </button>
              <transition name="slide">
                <!-- End-of-day registration: the form registers absences only -->
                <absence-form
                  v-if="showManualForm && endOfDayEnabled"
                  class="mt-2"
                  style="max-width:520px"
                  :date="todayStr"
                  :user-id="isAdmin && selectedUserId && selectedUserId !== currentUserId ? selectedUserId : null"
                  :absence-types="absenceTypes"
                  @saved="onAbsenceFormSaved"
                  @cancel="showManualForm = false"
                />
                <div v-else-if="showManualForm" ref="manualForm" class="manual-form mt-2">
                  <div class="d-flex flex-wrap align-items-end gap-2 mb-2">
                    <div class="manual-field">
                      <label class="manual-label">{{ $t('timeRegistration.type') }}</label>
                      <select v-model="manualType" class="form-select form-select-sm" style="width:150px">
                        <option value="work">{{ $t('timeRegistration.workType') }}</option>
                        <option v-for="t in absenceTypes" :key="t.id" :value="'absence-' + t.id">{{ absenceLabel(t.type) }}</option>
                      </select>
                    </div>
                    <div class="manual-field">
                      <label class="manual-label">{{ manualType === 'work' ? $t('timeRegistration.date') : $t('timeRegistration.fromDate') }}</label>
                      <input v-model="manualDate" type="date" class="form-control form-control-sm" />
                    </div>

                    <!-- Absence: optional end date (range) + optional partial-day hours -->
                    <template v-if="manualType !== 'work'">
                      <div class="manual-field">
                        <label class="manual-label">{{ $t('timeRegistration.toDate') }} <span class="text-secondary">({{ $t('common.optional') }})</span></label>
                        <input v-model="manualDateTo" type="date" class="form-control form-control-sm" />
                      </div>
                      <div class="manual-field" style="width:130px">
                        <label class="manual-label">{{ $t('timeRegistration.absenceHoursLabel') }} <span class="text-secondary">({{ $t('common.optional') }})</span></label>
                        <input v-model="manualAbsenceHours" type="number" step="0.5" min="0.5" max="23.5" class="form-control form-control-sm" :placeholder="$t('timeRegistration.fullDayPlaceholder')" />
                      </div>
                    </template>

                    <!-- Simplified: the user's group is the same for every row -->
                    <div v-if="manualType === 'work' && simplifiedEnabled" class="manual-field" style="flex:1;min-width:160px">
                      <label class="manual-label">{{ $t('timeRegistration.groupLabel') }}</label>
                      <input :value="myGroup ? myGroup.name : $t('timeRegistration.noGroupAssigned')" type="text" readonly class="form-control form-control-sm bg-light" />
                    </div>
                  </div>

                  <!-- Work: one or more rows for the chosen date, saved together -->
                  <template v-if="manualType === 'work'">
                    <div v-for="(row, i) in manualRows" :key="row.key" class="manual-row d-flex flex-wrap align-items-end gap-2 mb-2">
                      <!-- Classic work entry: project + clock-in/out required -->
                      <template v-if="!simplifiedEnabled">
                        <div class="manual-field">
                          <label class="manual-label">{{ $t('timeRegistration.inTime') }}</label>
                          <input v-model="row.clockIn" type="time" lang="en-GB" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field">
                          <label class="manual-label">{{ $t('timeRegistration.outTime') }}</label>
                          <input v-model="row.clockOut" type="time" lang="en-GB" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field" style="flex:1;min-width:160px">
                          <label class="manual-label">{{ $t('timeRegistration.project') }}</label>
                          <!-- Recently used projects until the user types, then a server-side search on number/name -->
                          <el-select
                            v-model="row.projectId"
                            filterable
                            remote
                            :remote-method="q => searchManualProjects(row, q)"
                            :loading="row.projectsLoading"
                            :placeholder="$t('endOfDay.searchProject')"
                            :no-data-text="$t('endOfDay.noProjectsFound')"
                            size="small"
                            @change="onManualProjectChanged(row)"
                          >
                            <el-option v-for="p in row.projectOptions || manualRecentProjects" :key="p.id" :value="p.id" :label="p.key ? '[' + p.key + '] ' + p.name : p.name" />
                          </el-select>
                        </div>
                        <div v-if="phasesEnabled && row.projectPhases.length" class="manual-field" style="flex:1;min-width:140px">
                          <label class="manual-label">{{ $t('projects.phase') }}<span v-if="rowPhaseRequired(row)"> *</span></label>
                          <select v-model="row.projectPhaseId" class="form-select form-select-sm">
                            <option :value="null" :disabled="rowPhaseRequired(row)">{{ rowPhaseRequired(row) ? $t('projects.selectPhasePlaceholder') : $t('projects.wholeProject') }}</option>
                            <option v-for="ph in row.projectPhases" :key="ph.id" :value="ph.id">{{ ph.name }}</option>
                          </select>
                        </div>
                      </template>

                      <!-- Simplified work entry: 4 hour fields + optional clock-in/out -->
                      <template v-else>
                        <div class="manual-field" style="width:110px">
                          <label class="manual-label">{{ $t('timeRegistration.normalHours') }}</label>
                          <input v-model="row.normalHours" type="number" step="0.25" min="0" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field" style="width:110px">
                          <label class="manual-label">{{ $t('timeRegistration.afterHours') }}</label>
                          <input v-model="row.afterHours" type="number" step="0.25" min="0" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field" style="width:110px">
                          <label class="manual-label">{{ $t('timeRegistration.overtimeHours') }}</label>
                          <input v-model="row.overtimeHours" type="number" step="0.25" min="0" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field" style="width:110px">
                          <label class="manual-label">{{ $t('timeRegistration.redDayHours') }}</label>
                          <input v-model="row.redDayHours" type="number" step="0.25" min="0" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field">
                          <label class="manual-label">{{ $t('timeRegistration.inTime') }} <span class="text-secondary">({{ $t('common.optional') }})</span></label>
                          <input v-model="row.clockInOptional" type="time" lang="en-GB" class="form-control form-control-sm" />
                        </div>
                        <div class="manual-field">
                          <label class="manual-label">{{ $t('timeRegistration.outTime') }} <span class="text-secondary">({{ $t('common.optional') }})</span></label>
                          <input v-model="row.clockOutOptional" type="time" lang="en-GB" class="form-control form-control-sm" />
                        </div>
                      </template>

                      <div v-if="commentsEnabled" class="manual-field" style="flex:1;min-width:160px">
                        <label class="manual-label">{{ $t('timeRegistration.comment') }} <span class="text-secondary">({{ $t('common.optional') }})</span></label>
                        <input v-model="row.comment" type="text" class="form-control form-control-sm" :placeholder="$t('timeRegistration.commentPlaceholder')" />
                      </div>
                      <button v-if="manualRows.length > 1" class="btn btn-sm btn-link text-secondary px-1 mb-0" :title="$t('timeRegistration.removeRow')" @click="manualRows.splice(i, 1)">
                        <i class="material-icons" style="font-size:1.2rem;vertical-align:middle">close</i>
                      </button>
                    </div>
                  </template>

                  <div class="d-flex flex-wrap align-items-center gap-2">
                    <button v-if="manualType === 'work' && manualRows.length < maxManualRows" class="btn btn-sm btn-outline-dark mb-0" @click="addManualRow">
                      <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">add</i>{{ $t('timeRegistration.addRow') }}
                    </button>
                    <span v-if="manualType === 'work' && manualRows.length > 1" class="text-sm text-secondary">
                      {{ $t('timeRegistration.total') }}: <strong>{{ formatDecimal(manualDayTotalHours) }}h</strong>
                    </span>
                    <button class="btn btn-sm btn-dark mb-0 ms-auto" :disabled="manualSubmitDisabled" @click="submitManualEntry">
                      <span v-if="manualSaving" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="material-icons me-1" style="font-size:1rem;vertical-align:middle">check</i>
                      {{ $t('common.save') }}
                    </button>
                  </div>
                </div>
              </transition>
            </div>

            <!-- ── Loading ─────────────────────────────────── -->
            <div v-if="overviewLoading" class="text-center py-5"><div class="spinner-border text-secondary" role="status"></div></div>

            <!-- ══ Personal View ═══════════════════════════════ -->
            <template v-if="!overviewLoading && currentView === 'personal'">
              <div v-if="personalDays.length === 0" class="text-center text-secondary py-4">{{ $t('timeRegistration.noEntriesForPeriod') }}</div>
              <template v-for="day in personalDays" :key="day.date">
              <!-- Scheduled day with nothing registered (EnableUnfinishedTimeRegistrationDaysInOverview) -->
              <div v-if="day.missing" class="day-card day-card-missing mb-3" :class="{ 'is-today': day.date.substring(0, 10) === todayStr }">
                <div class="day-card-header">
                  <div class="day-head-main">
                    <div class="day-weekday">{{ formatDayLabel(day.date) }}</div>
                    <div class="day-date-num">{{ formatDateLarge(day.date) }}</div>
                  </div>
                  <div class="day-head-totals d-flex align-items-center gap-2">
                    <span class="missing-label">{{ $t('timeRegistration.missingRegistration') }}</span>
                    <button class="btn btn-sm btn-outline-secondary mb-0" @click="registerMissingDay(day.date)">
                      <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">edit</i>
                      <span class="d-none d-sm-inline ms-1">{{ $t('endOfDay.registerDayTab') }}</span>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="day-card mb-3" :class="{ 'absence-card': dayHasAbsence(day), 'day-card-unfinished': dayShortfallHours(day) > 0, 'is-today': String(day.date).substring(0, 10) === todayStr }">
                <div class="day-card-header">
                  <div class="day-head-main">
                    <div class="day-weekday">{{ formatDayLabel(day.date) }}</div>
                    <div class="day-date-num">{{ formatDateLarge(day.date) }}</div>
                    <!-- Registered hours fall short of the schedule (EnableUnfinishedTimeRegistrationDaysInOverview) -->
                    <div v-if="dayShortfallHours(day) > 0" class="unfinished-label">{{ $t('timeRegistration.unfinishedHours', { hours: formatDecimal(dayShortfallHours(day)) }) }}</div>
                  </div>
                  <!-- Day totals: simplified (hour breakdown) vs classic (duration + overtime) -->
                  <div v-if="dayHasSimplifiedWork(day)" class="day-head-totals">
                    <div class="day-total-label">{{ $t('timeRegistration.workHours') }}</div>
                    <div class="day-total-value">{{ formatDecimal(dayHourTotals(day).total) }} {{ $t('timeRegistration.hrs') }}</div>
                    <div class="hour-breakdown-chips mt-1">
                      <span class="chip chip-normal">V {{ formatDecimal(dayHourTotals(day).normal) }}</span>
                      <span class="chip chip-after">E {{ formatDecimal(dayHourTotals(day).after) }}</span>
                      <span class="chip chip-overtime">Y {{ formatDecimal(dayHourTotals(day).overtime) }}</span>
                      <span class="chip chip-redday">S {{ formatDecimal(dayHourTotals(day).redDay) }}</span>
                    </div>
                  </div>
                  <div v-else class="day-head-totals">
                    <!-- Hide the zero work-hours figure on pure absence days; show absence hours instead -->
                    <template v-if="!dayHasAbsence(day) || dayRealMinutes(day) > 0">
                      <div class="day-total-label">{{ $t('timeRegistration.workHours') }}</div>
                      <div class="day-total-value">{{ minutesToDecimal(dayRealMinutes(day)) }} {{ $t('timeRegistration.hrs') }}</div>
                      <template v-if="dayRealOT(day) > 0">
                        <div class="day-total-label mt-1">{{ $t('timeRegistration.overtimeHours') }}</div>
                        <div class="day-total-value text-warning">{{ minutesToDecimal(dayRealOT(day)) }} {{ $t('timeRegistration.hrs') }}</div>
                      </template>
                    </template>
                    <template v-if="dayHasAbsence(day)">
                      <div class="day-total-label" :class="{ 'mt-1': dayRealMinutes(day) > 0 }">{{ $t('timeRegistration.absence') }}</div>
                      <div class="day-total-value absence-row-hours">{{ formatDecimal(dayAbsenceHours(day)) }} {{ $t('timeRegistration.hrs') }}</div>
                    </template>
                  </div>
                  <!-- Arrival times: only shown for days containing classic clocked entries -->
                  <div v-if="dayWorkEntries(day).length > 0 && dayHasClassicWork(day)" class="day-arrival">
                    <i class="material-icons time-icon">login</i>
                    <native-time-input class="time-input" :model-value="dayEarliestClockIn(day)" :save="val => onDayClockInChange(day, val)" />
                    <span class="time-sep">–</span>
                    <i class="material-icons time-icon">logout</i>
                    <native-time-input class="time-input" :model-value="dayLatestClockOut(day)" :save="val => onDayClockOutChange(day, val)" />
                  </div>
                </div>
                <div class="day-card-body">
                  <div v-for="entry in day.entries" :key="entry.id" class="entry-block">
                    <!-- Absence -->
                    <div v-if="entry.isAbsence" class="absence-row">
                      <div class="absence-row-icon"><i class="material-icons">event_busy</i></div>
                      <div class="flex-grow-1">
                        <div class="fw-bold">{{ absenceLabel(entry.timesheetEntryType) }}</div>
                        <div class="text-secondary text-xs">{{ absenceDayLabel(entry) }}</div>
                      </div>
                      <span class="absence-row-hours">{{ formatDecimal(entry.absenceHours != null ? entry.absenceHours : 8) }} {{ $t('timeRegistration.hrs') }}</span>
                      <button class="btn-icon text-secondary ms-2" :title="$t('timeRegistration.editEntryTitle')" @click="openEntryEditor(entry)"><i class="material-icons">edit</i></button>
                      <button v-if="isAdmin || ownEntryEditsEnabled" class="btn-icon text-danger ms-2" @click="deleteEntry(entry.id)"><i class="material-icons">delete</i></button>
                    </div>
                    <!-- Simplified work entry: group + 3 hour figures, editable via dialog -->
                    <div v-else-if="isSimplifiedEntry(entry)">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <i class="material-icons text-secondary" style="font-size:1rem">groups</i>
                        <span class="fw-bold">{{ entry.timeRegistrationGroupName }}</span>
                        <span v-if="entry.overriddenByUserName" class="text-muted text-xs ms-auto me-2">{{ $t('timeRegistration.overriddenBy') }} {{ entry.overriddenByUserName }}</span>
                        <button class="btn btn-sm btn-outline-secondary" :class="{ 'ms-auto': !entry.overriddenByUserName }" @click="openHoursEditModal(entry)">
                          <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">edit</i>
                          <span class="d-none d-sm-inline ms-1">{{ $t('timeRegistration.editHours') }}</span>
                        </button>
                        <button v-if="isAdmin || ownEntryEditsEnabled" class="btn-icon text-danger" @click="deleteEntry(entry.id)"><i class="material-icons">delete</i></button>
                      </div>
                      <div v-if="entry.realClockIn || entry.realClockOut" class="time-row">
                        <i class="material-icons time-icon">login</i>
                        <div class="time-cell">
                          <s v-if="entry.originalRealClockIn && formatTimeInput(entry.originalRealClockIn) !== formatTimeInput(entry.realClockIn)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockIn) }}</s>
                          <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockIn)" :save="val => onTimeChange(entry, 'realClockIn', val)" />
                        </div>
                        <span class="time-sep">–</span>
                        <i class="material-icons time-icon">logout</i>
                        <div class="time-cell">
                          <s v-if="entry.originalRealClockOut && formatTimeInput(entry.originalRealClockOut) !== formatTimeInput(entry.realClockOut)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockOut) }}</s>
                          <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockOut)" :save="val => onTimeChange(entry, 'realClockOut', val)" />
                        </div>
                        <span class="time-dur">({{ formatTimeSpan(entry.realClockIn, entry.realClockOut) }})</span>
                      </div>
                      <div class="hour-breakdown-chips d-none d-md-flex mt-2">
                        <span class="chip chip-normal">{{ $t('timeRegistration.normalHours') }}: <strong>{{ formatDecimal(entry.normalHours) }}</strong></span>
                        <span class="chip chip-after">{{ $t('timeRegistration.afterHours') }}: <strong>{{ formatDecimal(entry.afterHours) }}</strong></span>
                        <span class="chip chip-overtime">{{ $t('timeRegistration.overtimeHours') }}: <strong>{{ formatDecimal(entry.overtimeHours) }}</strong></span>
                        <span class="chip chip-redday">{{ $t('timeRegistration.redDayHours') }}: <strong>{{ formatDecimal(entry.redDayHours) }}</strong></span>
                      </div>
                      <div v-if="commentsEnabled && entry.comment" class="text-secondary text-xs mt-1">
                        <i class="material-icons" style="font-size:0.85rem;vertical-align:middle">comment</i> {{ entry.comment }}
                      </div>
                    </div>
                    <!-- Hours-only work entry (end-of-day registration): project + entered dagvinna/yfirvinna -->
                    <div v-else-if="isHoursOnlyEntry(entry)">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span class="fw-bold"><span v-if="entry.projectKey" class="text-secondary me-1">[{{ entry.projectKey }}]</span>{{ entry.projectName }}<span v-if="entry.projectPhaseName" class="text-secondary fw-normal"> — {{ entry.projectPhaseName }}</span></span>
                        <button class="btn btn-sm btn-outline-secondary ms-auto" @click="openEntryEditor(entry)">
                          <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">edit</i>
                          <span class="d-none d-sm-inline ms-1">{{ $t('timeRegistration.editHours') }}</span>
                        </button>
                        <button v-if="isAdmin || ownEntryEditsEnabled" class="btn-icon text-danger" @click="deleteEntry(entry.id)"><i class="material-icons">delete</i></button>
                      </div>
                      <div class="hour-breakdown-chips mt-1">
                        <span class="chip chip-normal">{{ $t('timeRegistration.normalHours') }}: <strong>{{ formatDecimal(entry.normalHours) }}</strong></span>
                        <span class="chip chip-overtime">{{ $t('timeRegistration.overtimeHours') }}: <strong>{{ formatDecimal(entry.overtimeHours) }}</strong></span>
                      </div>
                      <div v-if="entry.comment" class="text-secondary text-xs mt-1">
                        <i class="material-icons" style="font-size:0.85rem;vertical-align:middle">comment</i> {{ entry.comment }}
                      </div>
                    </div>
                    <!-- Classic work entry: project + real/adjusted clock times -->
                    <div v-else>
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span class="fw-bold"><span v-if="entry.projectKey" class="text-secondary me-1">[{{ entry.projectKey }}]</span>{{ entry.projectName }}<span v-if="entry.projectPhaseName" class="text-secondary fw-normal"> — {{ entry.projectPhaseName }}</span></span>
                        <span v-if="entry.wasAutoClockedOut" class="badge bg-warning text-dark" :title="$t('timeRegistration.autoClockedOutHint')">{{ $t('timeRegistration.autoClockedOut') }}</span>
                        <span v-if="entry.overriddenByUserName" class="text-muted text-xs ms-auto me-2">{{ $t('timeRegistration.overriddenBy') }} {{ entry.overriddenByUserName }}</span>
                        <button v-if="(isAdmin || ownEntryEditsEnabled) && allowAdminMoveTimesheetEntry" class="btn btn-sm btn-outline-primary" :class="{ 'ms-auto': !entry.overriddenByUserName }" @click="openMoveModal(entry)">
                          <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">swap_horiz</i>
                          <span class="d-none d-sm-inline ms-1">{{ $t('timeRegistration.moveToProject') }}</span>
                        </button>
                        <button v-if="isAdmin || ownEntryEditsEnabled" class="btn-icon text-danger" @click="deleteEntry(entry.id)"><i class="material-icons">delete</i></button>
                      </div>
                      <div class="time-row">
                        <span class="time-label">{{ $t('timeRegistration.realTime') }}:</span>
                        <i class="material-icons time-icon">login</i>
                        <div class="time-cell">
                          <s v-if="entry.originalRealClockIn && formatTimeInput(entry.originalRealClockIn) !== formatTimeInput(entry.realClockIn)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockIn) }}</s>
                          <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockIn)" :save="val => onTimeChange(entry, 'realClockIn', val)" />
                        </div>
                        <span class="time-sep">–</span>
                        <i class="material-icons time-icon">logout</i>
                        <div class="time-cell">
                          <s v-if="entry.originalRealClockOut && formatTimeInput(entry.originalRealClockOut) !== formatTimeInput(entry.realClockOut)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockOut) }}</s>
                          <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockOut)" :save="val => onTimeChange(entry, 'realClockOut', val)" />
                        </div>
                        <span class="time-dur">({{ formatTimeSpan(entry.realClockIn, entry.realClockOut) }})</span>
                      </div>
                      <div v-if="isAdmin && adjustmentsEnabled" class="time-row">
                        <span class="time-label text-info">{{ $t('timeRegistration.adjusted') }}:</span>
                        <i class="material-icons time-icon text-info">login</i>
                        <div class="time-cell">
                          <input type="time" lang="en-GB" class="time-input adj" :value="formatTimeInput(entry.adjustedClockIn)" disabled />
                        </div>
                        <span class="time-sep">–</span>
                        <i class="material-icons time-icon text-info">logout</i>
                        <div class="time-cell">
                          <input type="time" lang="en-GB" class="time-input adj" :value="formatTimeInput(entry.adjustedClockOut)" disabled />
                        </div>
                        <span class="time-dur text-info">({{ formatTimeSpan(entry.adjustedClockIn, entry.adjustedClockOut) }})</span>
                      </div>
                      <div v-if="entry.deductedBreakMinutes" class="text-secondary text-xs mt-1">
                        <i class="material-icons" style="font-size:0.85rem;vertical-align:middle">free_breakfast</i>
                        {{ $t('timeRegistration.breakDeducted') }}: {{ entry.deductedBreakMinutes }} {{ $t('timeRegistration.minutesShort') }}
                      </div>
                      <div v-if="commentsEnabled" class="mt-1">
                        <input
                          type="text"
                          class="form-control form-control-sm comment-input"
                          :value="entry.comment || ''"
                          :placeholder="$t('timeRegistration.commentPlaceholder')"
                          @blur="onCommentChange(entry, $event.target.value)"
                          @keydown.enter.prevent="$event.target.blur()"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </template>
            </template>

            <!-- ══ Project View ═════════════════════════════════ -->
            <template v-if="!overviewLoading && currentView === 'project'">
              <div v-if="projectDays.length === 0" class="text-center text-secondary py-4">{{ $t('timeRegistration.noEntriesFound') }}</div>
              <div v-if="erpSyncResult" class="alert alert-dismissible p-2 pe-5 text-sm mb-3" :class="erpSyncResult.failures.length || erpSyncResult.errorMessage ? 'alert-warning' : 'alert-success'">
                <button type="button" class="btn-close p-2" style="font-size:0.7rem" @click="erpSyncResult = null"></button>
                <div v-if="erpSyncResult.errorMessage" class="fw-semibold">{{ erpSyncResult.errorMessage }}</div>
                <div>{{ erpSyncResultText }}</div>
                <ul v-if="erpSyncResult.failures.length" class="mb-0 mt-1">
                  <li v-for="f in erpSyncResult.failures" :key="f.timesheetEntryId">{{ erpSyncEntryLabels[f.timesheetEntryId] || f.timesheetEntryId }} — {{ f.details }}</li>
                </ul>
              </div>

              <div v-for="day in projectDays" :key="day.date" class="day-card mb-3">
                <div class="day-card-header">
                  <div class="day-head-main">
                    <div class="day-weekday">{{ formatDayLabel(day.date) }}</div>
                    <div class="day-date-num">{{ formatDateLarge(day.date) }}</div>
                    <div v-if="isAdmin" class="text-secondary text-sm">{{ day.workerCount }} {{ $t('timeRegistration.employees') }}</div>
                  </div>
                  <div class="day-head-totals">
                    <div class="day-total-label">{{ $t('timeRegistration.workHours') }}</div>
                    <div class="day-total-value">{{ minutesToDecimal(day.totalAdjustedMinutes) }} {{ $t('timeRegistration.hrs') }}</div>
                    <template v-if="day.totalOvertimeMinutes > 0">
                      <div class="day-total-label mt-1">{{ $t('timeRegistration.overtimeHours') }}</div>
                      <div class="day-total-value text-warning">{{ minutesToDecimal(day.totalOvertimeMinutes) }} {{ $t('timeRegistration.hrs') }}</div>
                    </template>
                  </div>
                </div>
                <div class="day-card-body">
                  <template v-for="(worker, wi) in day.workers" :key="wi">
                    <div class="worker-block" :class="{ 'mt-3': wi > 0 }">
                      <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span class="fw-bold">{{ worker.userName }}</span>
                        <div class="ms-auto text-end d-flex gap-3 align-items-center">
                          <span class="text-xs text-secondary">{{ $t('timeRegistration.workHoursShort') }} <strong class="text-dark">{{ minutesToDecimal(worker.adjustedMinutes) }} {{ $t('timeRegistration.hrs') }}</strong></span>
                          <span v-if="worker.overtime > 0" class="text-xs">
                            <span class="text-warning">{{ $t('timeRegistration.overtimeHours') }}</span> <strong class="text-warning">{{ minutesToDecimal(worker.overtime) }} {{ $t('timeRegistration.hrs') }}</strong>
                          </span>
                        </div>
                      </div>
                      <div v-for="entry in worker.entries" :key="entry.id" class="ms-2 entry-block">
                        <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <span class="text-secondary text-sm">{{ entry.projectKey ? '[' + entry.projectKey + '] ' : '' }}{{ entry.projectName }}{{ entry.projectPhaseName ? ' — ' + entry.projectPhaseName : '' }}</span>
                          <span v-if="erpTimeSyncEnabled" class="text-xs" :class="erpSyncStatus(entry).cls" :title="erpSyncStatus(entry).title">
                            <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">{{ erpSyncStatus(entry).icon }}</i>
                            {{ erpSyncStatus(entry).text }}
                          </span>
                          <!-- The default item's option carries value "" so picking it stores null = "follow the default" -->
                          <select v-if="bcTimeSync && isAdmin && !entry.isAbsence" class="form-select form-select-sm" style="width:auto;max-width:240px" :value="entry.erpItemCode && entry.erpItemCode !== bcDefaultItem ? entry.erpItemCode : ''" :title="$t('timeRegistration.bcItemCode')" @change="onBcItemChange(entry, $event.target.value)">
                            <option v-if="!bcDefaultItem" value="">{{ $t('timeRegistration.bcItemNone') }}</option>
                            <option v-for="it in bcItems" :key="it.number" :value="it.number === bcDefaultItem ? '' : it.number">[{{ it.number }}] {{ it.displayName }}{{ it.number === bcDefaultItem ? ' ' + $t('timeRegistration.bcItemDefaultSuffix') : '' }}</option>
                          </select>
                          <span v-if="entry.overriddenByUserName" class="text-muted text-xs ms-auto me-1">{{ $t('timeRegistration.overriddenBy') }} {{ entry.overriddenByUserName }}</span>
                          <button v-if="allowAdminMoveTimesheetEntry" class="btn btn-sm btn-outline-primary ms-auto" @click="openMoveModal(entry)">
                            <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">swap_horiz</i>
                            <span class="d-none d-sm-inline ms-1">{{ $t('timeRegistration.moveToProject') }}</span>
                          </button>
                          <!-- Hours-only entries have no inline time inputs — admins correct the line in a dialog -->
                          <button v-if="isAdmin && isHoursOnlyEntry(entry)" class="btn btn-sm btn-outline-secondary" :class="{ 'ms-auto': !allowAdminMoveTimesheetEntry && !entry.overriddenByUserName }" @click="openEntryEditor(entry)">
                            <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">edit</i>
                            <span class="d-none d-sm-inline ms-1">{{ $t('timeRegistration.editHours') }}</span>
                          </button>
                        </div>
                        <div v-if="isHoursOnlyEntry(entry)" class="hour-breakdown-chips mt-1">
                          <span class="chip chip-normal">{{ $t('timeRegistration.normalHours') }}: <strong>{{ formatDecimal(entry.normalHours) }}</strong></span>
                          <span class="chip chip-overtime">{{ $t('timeRegistration.overtimeHours') }}: <strong>{{ formatDecimal(entry.overtimeHours) }}</strong></span>
                          <span v-if="entry.comment" class="text-secondary text-xs align-self-center">{{ entry.comment }}</span>
                        </div>
                        <div v-else class="time-row">
                          <span class="time-label">{{ $t('timeRegistration.realTime') }}:</span>
                          <i class="material-icons time-icon">login</i>
                          <div class="time-cell">
                            <s v-if="entry.originalRealClockIn && formatTimeInput(entry.originalRealClockIn) !== formatTimeInput(entry.realClockIn)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockIn) }}</s>
                            <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockIn)" :disabled="!isAdmin" :save="val => onTimeChange(entry, 'realClockIn', val)" />
                          </div>
                          <span class="time-sep">–</span>
                          <i class="material-icons time-icon">logout</i>
                          <div class="time-cell">
                            <s v-if="entry.originalRealClockOut && formatTimeInput(entry.originalRealClockOut) !== formatTimeInput(entry.realClockOut)" class="text-muted text-sm">{{ formatTimeInput(entry.originalRealClockOut) }}</s>
                            <native-time-input class="time-input" :model-value="formatTimeInput(entry.realClockOut)" :disabled="!isAdmin" :save="val => onTimeChange(entry, 'realClockOut', val)" />
                          </div>
                          <span class="time-dur">({{ formatTimeSpan(entry.realClockIn, entry.realClockOut) }})</span>
                        </div>
                        <div v-if="isAdmin && adjustmentsEnabled && !isHoursOnlyEntry(entry)" class="time-row">
                          <span class="time-label text-info">{{ $t('timeRegistration.adjusted') }}:</span>
                          <i class="material-icons time-icon text-info">login</i>
                          <div class="time-cell">
                            <input type="time" lang="en-GB" class="time-input adj" :value="formatTimeInput(entry.adjustedClockIn)" disabled />
                          </div>
                          <span class="time-sep">–</span>
                          <i class="material-icons time-icon text-info">logout</i>
                          <div class="time-cell">
                            <input type="time" lang="en-GB" class="time-input adj" :value="formatTimeInput(entry.adjustedClockOut)" disabled />
                          </div>
                          <span class="time-dur text-info">({{ formatTimeSpan(entry.adjustedClockIn, entry.adjustedClockOut) }})</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>

            <!-- ══ Absence View (Fjarverusýn) ═══════════════════ -->
            <template v-if="!overviewLoading && currentView === 'absence'">
              <div v-if="absenceDays.length === 0" class="text-center text-secondary py-4">{{ $t('timeRegistration.noAbsencesForPeriod') }}</div>
              <div v-if="erpSyncResult" class="alert alert-dismissible p-2 pe-5 text-sm mb-3" :class="erpSyncResult.failures.length || erpSyncResult.errorMessage ? 'alert-warning' : 'alert-success'">
                <button type="button" class="btn-close p-2" style="font-size:0.7rem" @click="erpSyncResult = null"></button>
                <div v-if="erpSyncResult.errorMessage" class="fw-semibold">{{ erpSyncResult.errorMessage }}</div>
                <div>{{ erpSyncResultText }}</div>
                <ul v-if="erpSyncResult.failures.length" class="mb-0 mt-1">
                  <li v-for="f in erpSyncResult.failures" :key="f.timesheetEntryId">{{ erpSyncEntryLabels[f.timesheetEntryId] || f.timesheetEntryId }} — {{ f.details }}</li>
                </ul>
              </div>

              <div v-for="day in absenceDays" :key="day.date" class="day-card mb-3">
                <div class="day-card-header">
                  <div class="day-head-main">
                    <div class="day-weekday">{{ formatDayLabel(day.date) }}</div>
                    <div class="day-date-num">{{ formatDateLarge(day.date) }}</div>
                    <div class="text-secondary text-sm">{{ day.workers.length }} {{ $t('timeRegistration.employees') }}</div>
                  </div>
                </div>
                <div class="day-card-body">
                  <template v-for="(worker, wi) in day.workers" :key="wi">
                    <div class="worker-block" :class="{ 'mt-3': wi > 0 }">
                      <div class="fw-bold mb-1">{{ worker.userName }}</div>
                      <div v-for="entry in worker.entries" :key="entry.id" class="ms-2 entry-block d-flex align-items-center gap-2 flex-wrap">
                        <i class="material-icons text-secondary" style="font-size:1rem">event_busy</i>
                        <span class="fw-semibold">{{ absenceLabel(entry.timesheetEntryType) }}</span>
                        <span class="text-secondary text-sm">{{ absenceHoursLabel(entry) }}</span>
                        <span v-if="entry.comment" class="text-secondary text-sm">— {{ entry.comment }}</span>
                        <span v-if="absenceSyncEnabled" class="text-xs ms-auto" :class="erpSyncStatus(entry).cls" :title="erpSyncStatus(entry).title">
                          <i class="material-icons" style="font-size:0.9rem;vertical-align:middle">{{ erpSyncStatus(entry).icon }}</i>
                          {{ erpSyncStatus(entry).text }}
                        </span>
                        <button class="btn-icon text-secondary" :class="{ 'ms-auto': !absenceSyncEnabled }" :title="$t('timeRegistration.editEntryTitle')" @click="openEntryEditor(entry)"><i class="material-icons">edit</i></button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>

          <!-- ── Print report — simplified mode (hour breakdown) ────── -->
          <div v-if="activeTab === 1 && simplifiedEnabled && !allUsersReportActive" class="print-only print-report">
            <div class="print-header">
              <div>
                <h2 class="mb-1">{{ $t('timeRegistration.timeReport') }}</h2>
                <p v-if="selectedUserName" class="mb-0"><strong>{{ $t('timeRegistration.employee') }}:</strong> {{ selectedUserName }}</p>
                <p v-if="myGroup" class="mb-0"><strong>{{ $t('timeRegistration.groupLabel') }}:</strong> {{ myGroup.name }}</p>
                <p class="mb-0"><strong>{{ $t('timeRegistration.periodLabel') }}:</strong> {{ dateRangeLabel }}</p>
              </div>
              <div class="text-end">
                <p class="mb-0"><strong>{{ $t('timeRegistration.totalHours') }}:</strong> {{ formatDecimal(simplifiedPeriodTotals().total) }} {{ $t('timeRegistration.hrs') }}</p>
              </div>
            </div>
            <table class="print-table">
              <thead>
                <tr>
                  <th>{{ $t('timeRegistration.dayColumn') }}</th>
                  <th>{{ $t('timeRegistration.groupLabel') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.normalHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.afterHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.overtimeHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.redDayHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.totalHours') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="day in days" :key="'sp-' + day.date">
                  <tr v-for="entry in day.entries.filter(e => !e.isAbsence && isSimplifiedEntry(e))" :key="'spe-' + entry.id">
                    <td>{{ formatDateLarge(day.date) }}</td>
                    <td>{{ entry.timeRegistrationGroupName }}</td>
                    <td class="text-end">{{ formatDecimal(entry.normalHours) }}</td>
                    <td class="text-end">{{ formatDecimal(entry.afterHours) }}</td>
                    <td class="text-end">{{ formatDecimal(entry.overtimeHours) }}</td>
                    <td class="text-end">{{ formatDecimal(entry.redDayHours) }}</td>
                    <td class="text-end">{{ formatDecimal(simplifiedEntryTotalHours(entry)) }}</td>
                  </tr>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2"><strong>{{ $t('timeRegistration.total') }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(simplifiedPeriodTotals().normal) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(simplifiedPeriodTotals().after) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(simplifiedPeriodTotals().overtime) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(simplifiedPeriodTotals().redDay) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(simplifiedPeriodTotals().total) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Print report — simplified mode, all users (admin) ── -->
          <div v-if="activeTab === 1 && simplifiedEnabled && isAdmin && allUsersReportActive" class="print-only print-report">
            <div class="print-header">
              <div>
                <h2 class="mb-1">{{ $t('timeRegistration.allUsersReportTitle') }}</h2>
                <p class="mb-0"><strong>{{ $t('timeRegistration.periodLabel') }}:</strong> {{ dateRangeLabel }}</p>
              </div>
              <div class="text-end">
                <p class="mb-0"><strong>{{ $t('timeRegistration.totalHours') }}:</strong> {{ formatDecimal(allUsersReportTotals.total) }} {{ $t('timeRegistration.hrs') }}</p>
              </div>
            </div>
            <table class="print-table">
              <thead>
                <tr>
                  <th>{{ $t('timeRegistration.employee') }}</th>
                  <th>{{ $t('timeRegistration.groupLabel') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.normalHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.afterHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.overtimeHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.redDayHoursShort') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.totalHours') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in allUsersReportRows" :key="'aur-' + row.userId">
                  <td>{{ row.userName }}</td>
                  <td>{{ row.groupNames }}</td>
                  <td class="text-end">{{ formatDecimal(row.normalHours) }}</td>
                  <td class="text-end">{{ formatDecimal(row.afterHours) }}</td>
                  <td class="text-end">{{ formatDecimal(row.overtimeHours) }}</td>
                  <td class="text-end">{{ formatDecimal(row.redDayHours) }}</td>
                  <td class="text-end">{{ formatDecimal(row.totalHours) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2"><strong>{{ $t('timeRegistration.total') }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(allUsersReportTotals.normal) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(allUsersReportTotals.after) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(allUsersReportTotals.overtime) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(allUsersReportTotals.redDay) }}</strong></td>
                  <td class="text-end"><strong>{{ formatDecimal(allUsersReportTotals.total) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Print report (classic mode, project view only) ───── -->
          <div v-if="activeTab === 1 && !simplifiedEnabled && currentView === 'project'" class="print-only print-report">
            <div class="print-header">
              <div>
                <h2 class="mb-1">{{ $t('timeRegistration.timeReport') }}</h2>
                <p v-if="projectViewProjectId" class="mb-0"><strong>{{ $t('timeRegistration.project') }}:</strong> {{ projectViewProjectId }}</p>
                <p class="mb-0"><strong>{{ $t('timeRegistration.periodLabel') }}:</strong> {{ dateRangeLabel }}</p>
              </div>
              <div class="text-end">
                <p class="mb-0"><strong>{{ $t('timeRegistration.totalWorkHours') }}:</strong> {{ minutesToDecimal(printStats.normal) }} {{ $t('timeRegistration.hrs') }}</p>
                <p v-if="includeOvertimeInReport && printStats.overtime > 0" class="mb-0"><strong>{{ $t('timeRegistration.totalOvertime') }}:</strong> {{ minutesToDecimal(printStats.overtime) }} {{ $t('timeRegistration.hrs') }}</p>
              </div>
            </div>
            <table class="print-table">
              <thead>
                <tr>
                  <th>{{ $t('timeRegistration.dayColumn') }}</th>
                  <th>{{ $t('timeRegistration.employee') }}</th>
                  <th>{{ $t('timeRegistration.inTime') }}</th>
                  <th>{{ $t('timeRegistration.outTime') }}</th>
                  <th class="text-end">{{ $t('timeRegistration.workHoursShort') }}</th>
                  <th v-if="includeOvertimeInReport" class="text-end">{{ $t('timeRegistration.overtimeHours') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="day in printProjectDays" :key="'p-' + day.date">
                  <template v-for="(worker, wi) in day.workers" :key="'pw-' + wi">
                    <tr v-for="(entry, ei) in worker.entries" :key="'pe-' + entry.id">
                      <td v-if="wi === 0 && ei === 0" :rowspan="day.totalEntries">{{ formatDateLarge(day.date) }}</td>
                      <td>{{ worker.userName }}</td>
                      <td>{{ formatTimeInput(isAdmin && adjustmentsEnabled && entry.adjustedClockIn ? entry.adjustedClockIn : entry.realClockIn) }}</td>
                      <td>{{ formatTimeInput(isAdmin && adjustmentsEnabled && entry.adjustedClockOut ? entry.adjustedClockOut : entry.realClockOut) }}</td>
                      <td class="text-end">{{ minutesToDecimal(entry._normalMins) }}</td>
                      <td v-if="includeOvertimeInReport" class="text-end">{{ entry._overtimeMins > 0 ? minutesToDecimal(entry._overtimeMins) : '' }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="4"><strong>{{ $t('timeRegistration.total') }}</strong></td>
                  <td class="text-end"><strong>{{ minutesToDecimal(printStats.normal) }}</strong></td>
                  <td v-if="includeOvertimeInReport" class="text-end"><strong>{{ printStats.overtime > 0 ? minutesToDecimal(printStats.overtime) : '' }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── ProjectPickModal ────────────────────────────── -->
          <project-pick-modal
            :show="showProjectPickModal" :status-filter="2" order-by="timeRegistration"
            @update:show="showProjectPickModal = $event" @confirm="onModalConfirm"
          />

          <!-- ── Edit today's entry (clock tab) — hidden while its project picker is open ── -->
          <div v-if="entryEdit.show && !showProjectPickModal" class="timeclock-modal-overlay" @click.self="entryEdit.show = false">
            <div class="timeclock-modal">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">edit</i>
                  {{ $t('timeRegistration.editEntry') }}
                </h6>
                <button class="btn btn-sm btn-link p-0 text-secondary" @click="entryEdit.show = false"><i class="material-icons">close</i></button>
              </div>
              <label class="form-label text-sm mb-1">{{ $t('timeRegistration.project') }}</label>
              <button class="btn btn-outline-secondary w-100 text-start d-flex align-items-center gap-2 mb-3" @click="openProjectModal('edit')">
                <span class="flex-grow-1 text-truncate">{{ entryEdit.projectLabel }}</span>
                <i class="material-icons" style="font-size:1.1rem">swap_horiz</i>
              </button>
              <div class="d-flex gap-2">
                <div class="flex-grow-1">
                  <label class="form-label text-sm mb-1">{{ $t('timeRegistration.inTime') }}</label>
                  <input v-model="entryEdit.clockIn" type="time" lang="en-GB" class="form-control" />
                </div>
                <div v-if="!entryEdit.isOpen" class="flex-grow-1">
                  <label class="form-label text-sm mb-1">{{ $t('timeRegistration.outTime') }}</label>
                  <input v-model="entryEdit.clockOut" type="time" lang="en-GB" class="form-control" />
                </div>
              </div>
              <p v-if="entryEdit.hasAdjacent" class="text-secondary text-xs mt-2 mb-0">{{ $t('timeRegistration.adjacentEntriesMoveHint') }}</p>
              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-outline-secondary flex-grow-1 mb-0" @click="entryEdit.show = false">{{ $t('common.cancel') }}</button>
                <button class="btn btn-dark flex-grow-1 mb-0" :disabled="entryEdit.saving" @click="saveEntryEdit">{{ $t('common.save') }}</button>
              </div>
            </div>
          </div>

          <!-- ── Move to Project Modal ──────────────────────── -->
          <div v-if="moveModal.show && allowAdminMoveTimesheetEntry" class="timeclock-modal-overlay" @click.self="moveModal.show = false">
            <div class="timeclock-modal">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">drive_file_move</i>
                  {{ $t('timeRegistration.moveToProject') }}
                </h6>
                <button class="btn btn-sm btn-link p-0 text-secondary" @click="moveModal.show = false"><i class="material-icons">close</i></button>
              </div>
              <p class="text-sm text-secondary mb-2">{{ $t('timeRegistration.currentProject') }}: <strong>{{ moveModal.currentProjectName }}</strong></p>
              <template v-if="!moveModal.selectedProject">
                <el-input v-model="moveModal.search" class="search-input mb-2" clearable prefix-icon="Search" :placeholder="$t('common.searchRecords')" />
                <div style="max-height:280px;overflow-y:auto">
                  <button
                    v-for="p in moveModalFilteredProjects"
                    :key="p.id"
                    class="btn btn-sm w-100 text-start mb-1"
                    :class="p.id === moveModal.currentProjectId ? 'btn-dark' : 'btn-outline-secondary'"
                    @click="pickMoveProject(p)"
                  >
                    {{ p.key ? '[' + p.key + '] ' : '' }}{{ p.name }}
                  </button>
                  <p v-if="moveModalFilteredProjects.length === 0" class="text-secondary text-sm mb-0 text-center py-2">{{ $t('timeRegistration.noEntriesFound') }}</p>
                </div>
              </template>
              <template v-else>
                <button class="btn btn-sm btn-link ps-0 mb-2 text-secondary" @click="moveModal.selectedProject = null">
                  <i class="material-icons" style="font-size:1rem;vertical-align:middle">arrow_back</i>
                  {{ moveModal.selectedProject.key ? '[' + moveModal.selectedProject.key + '] ' : '' }}{{ moveModal.selectedProject.name }}
                </button>
                <div style="max-height:280px;overflow-y:auto">
                  <div v-if="moveModal.loadingPhases" class="text-center py-2"><span class="spinner-border spinner-border-sm"></span></div>
                  <template v-else>
                    <button v-if="!moveModal.selectedProject.phaseRequired" class="btn btn-sm w-100 text-start mb-1 btn-outline-dark" @click="confirmMove(moveModal.selectedProject)">
                      {{ $t('projects.wholeProject') }}
                    </button>
                    <button v-for="ph in moveModal.phases" :key="ph.id" class="btn btn-sm w-100 text-start mb-1 btn-outline-secondary" @click="confirmMove(moveModal.selectedProject, ph.id)">
                      {{ ph.name }}
                    </button>
                  </template>
                </div>
              </template>
            </div>
          </div>

          <!-- ── Absence Modal ───────────────────────────────── -->
          <div v-if="showAbsenceModal" class="timeclock-modal-overlay" @click.self="showAbsenceModal = false">
            <div class="timeclock-modal">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">{{ $t('timeRegistration.registerAbsence') }}</h6>
                <button class="btn btn-sm btn-link p-0 text-secondary" @click="showAbsenceModal = false"><i class="material-icons">close</i></button>
              </div>
              <div class="mb-3">
                <select v-model="absenceTypeId" class="form-select">
                  <option value="" disabled>{{ $t('timeRegistration.selectAbsenceType') }}</option>
                  <option v-for="type in absenceTypes" :key="type.id" :value="type.id">{{ absenceLabel(type.type) }}</option>
                </select>
              </div>
              <div class="d-flex gap-2 mb-3">
                <div class="flex-grow-1">
                  <label class="form-label text-sm mb-1">{{ $t('timeRegistration.fromDate') }}</label>
                  <input v-model="absenceDateFrom" type="date" class="form-control" />
                </div>
                <div class="flex-grow-1">
                  <label class="form-label text-sm mb-1">{{ $t('timeRegistration.toDate') }}</label>
                  <input v-model="absenceDateTo" type="date" class="form-control" />
                </div>
              </div>
              <div v-if="!absenceIsRange" class="mb-3">
                <label class="form-label text-sm mb-1">{{ $t('timeRegistration.absenceHoursLabel') }}</label>
                <input v-model="absenceHours" type="number" step="0.5" min="0.5" max="8" class="form-control" />
              </div>
              <template v-else>
                <div class="d-flex gap-2 mb-2">
                  <div class="flex-grow-1">
                    <label class="form-label text-sm mb-1">{{ $t('timeRegistration.absenceFirstDayHours') }}</label>
                    <input v-model="absenceFirstDayHours" type="number" step="0.5" min="0.5" max="8" class="form-control" />
                  </div>
                  <div class="flex-grow-1">
                    <label class="form-label text-sm mb-1">{{ $t('timeRegistration.absenceLastDayHours') }}</label>
                    <input v-model="absenceLastDayHours" type="number" step="0.5" min="0.5" max="8" class="form-control" />
                  </div>
                </div>
                <p class="text-secondary text-xs mb-3">{{ $t('timeRegistration.absenceMiddleDaysFull') }}</p>
              </template>
              <button class="btn btn-dark w-100" :disabled="!absenceTypeId || !absenceDateFrom || (absenceIsRange ? (!absenceFirstDayHours || !absenceLastDayHours) : !absenceHours) || loading" @click="registerAbsence">
                <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">event_busy</i> {{ $t('timeRegistration.registerAbsence') }}
              </button>
            </div>
          </div>

          <!-- ── Edit Hours Modal (admin / user edits a simplified entry) ─ -->
          <div v-if="showHoursEditModal" class="timeclock-modal-overlay" @click.self="showHoursEditModal = false">
            <div class="timeclock-modal">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">edit</i>
                  {{ $t('timeRegistration.editHoursTitle') }}
                </h6>
                <button class="btn btn-sm btn-link p-0 text-secondary" @click="showHoursEditModal = false"><i class="material-icons">close</i></button>
              </div>
              <p v-if="hoursEditEntry" class="text-sm text-secondary mb-3">
                {{ hoursEditEntry.timeRegistrationGroupName }} — {{ formatDateLarge(hoursEditEntry.entryDate) }}
              </p>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.normalHours') }}</label>
                <input v-model="hoursEditForm.normal" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.afterHours') }}</label>
                <input v-model="hoursEditForm.after" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.overtimeHours') }}</label>
                <input v-model="hoursEditForm.overtime" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.redDayHours') }}</label>
                <input v-model="hoursEditForm.redDay" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div v-if="commentsEnabled" class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.comment') }} <span class="text-secondary text-sm">({{ $t('common.optional') }})</span></label>
                <input v-model="hoursEditForm.comment" type="text" class="form-control" :placeholder="$t('timeRegistration.commentPlaceholder')" />
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-outline-secondary flex-grow-1" :disabled="hoursEditSaving" @click="showHoursEditModal = false">
                  {{ $t('common.cancel') }}
                </button>
                <button class="btn btn-dark flex-grow-1" :disabled="hoursEditSaving" @click="submitHoursEdit">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">save</i>
                  {{ $t('common.save') }}
                </button>
              </div>
            </div>
          </div>

          <!-- ── Simplified Hours Modal (clock-out) ────────── -->
          <div v-if="showHoursModal" class="timeclock-modal-overlay" @click.self="showHoursModal = false">
            <div class="timeclock-modal">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">schedule</i>
                  {{ $t('timeRegistration.hoursBreakdownTitle') }}
                </h6>
                <button class="btn btn-sm btn-link p-0 text-secondary" @click="showHoursModal = false"><i class="material-icons">close</i></button>
              </div>
              <p class="text-sm text-secondary mb-3">{{ $t('timeRegistration.hoursBreakdownHint') }}</p>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.normalHours') }}</label>
                <input v-model="hoursForm.normal" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.afterHours') }}</label>
                <input v-model="hoursForm.after" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.overtimeHours') }}</label>
                <input v-model="hoursForm.overtime" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('timeRegistration.redDayHours') }}</label>
                <input v-model="hoursForm.redDay" type="number" step="0.25" min="0" class="form-control" />
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-outline-secondary flex-grow-1" :disabled="hoursSaving" @click="showHoursModal = false">
                  {{ $t('common.cancel') }}
                </button>
                <button class="btn btn-danger flex-grow-1" :disabled="hoursSaving" @click="submitHoursAndClockOut">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">logout</i>
                  {{ $t('timeRegistration.clockOut') }}
                </button>
              </div>
            </div>
          </div>

        </card>
      </div>
    </div>

    <!-- Edit one end-of-day work line or one absence (Persónusýn, Verkefnasýn, Fjarverusýn) -->
    <el-dialog v-model="entryEditor.open" :title="$t('timeRegistration.editEntryTitle')" width="min(480px, calc(100vw - 24px))" destroy-on-close>
      <edit-entry-dialog
        v-if="entryEditor.entry"
        :entry="entryEditor.entry"
        :user-id="entryEditor.entry.userId !== currentUserId ? entryEditor.entry.userId : null"
        :heading="entryEditorHeading"
        :absence-types="absenceTypes"
        :absence-label="absenceLabel"
        :cap-absence="endOfDayEnabled"
        :reference-enabled="referenceEnabledFor(entryEditor.entry.userId)"
        @saved="onEntryEdited"
        @cancel="entryEditor.open = false"
      />
    </el-dialog>

    <!-- End-of-day registration: register/correct a worker's day (hours-only lines) -->
    <el-dialog v-if="endOfDayEnabled" v-model="dayEditor.open" :title="$t('endOfDay.registerDayTab')" width="min(520px, calc(100vw - 24px))" destroy-on-close @closed="onDayEditorClosed">
      <div class="mb-3">
        <label class="form-label text-xs mb-0">{{ $t('timeRegistration.date') }}</label>
        <input v-model="dayEditor.date" type="date" class="form-control form-control-sm" :max="todayStr" />
      </div>
      <day-registration-editor
        v-if="dayEditor.date"
        :date="dayEditor.date"
        :user-id="dayEditor.userId !== currentUserId ? dayEditor.userId : null"
        :locked="dayEditorLocked"
        :phases-enabled="phasesEnabled"
        :reference-enabled="referenceEnabledFor(dayEditor.userId)"
        @saved="dayEditor.changed = true"
      />
    </el-dialog>
  </div>
</template>

<script>
import api from "../../services/api";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import swal from "sweetalert2";
import * as XLSX from "xlsx";
import Card from "../../components/Card.vue";
import NativeTimeInput from "../../components/NativeTimeInput.vue";
import ProjectPickModal from "../../components/Modals/ProjectPickModal.vue";
import DayRegistrationEditor from "./DayRegistrationEditor.vue";
import EditEntryDialog from "./EditEntryDialog.vue";
import DayRegistrationTab from "./DayRegistrationTab.vue";
import AbsenceForm from "./AbsenceForm.vue";
import { ElDialog } from "element-plus";
import { getCustomerConfig } from "../Settings/customerConfigHelper.js";
import { getUserConfig } from "../Settings/userConfigHelper";
import { loadCustomerMeCached, getErpDisplayName } from "../../services/customerMeHelper.js";
import { getCurrentSalaryPeriod, getPreviousSalaryPeriod, getWorkPeriod, formatSalaryPeriodLabel, formatWeekRangeLabel, getWeekRange, dateStr as fmtDateStr } from "../../utils/salaryPeriod.js";
import { weekdayName, weekdayShort, monthShort } from "../../utils/dateNames.js";

/**
 * TimeRegistration — Main time tracking component with two tabs:
 *   Tab 0 (Clock): Real-time clock widget for clocking in/out, switching projects, registering absence.
 *   Tab 1 (Overview): Personal and project views with date range filters, manual entry, and print reports.
 *
 * Key concepts:
 *   - adjustmentsEnabled: per-customer config controlling whether floor/ceil rules are applied
 *   - Admin users can view/edit other users' entries and see adjusted time columns (when enabled)
 *   - All user-facing text uses $t() i18n translations
 */

let manualRowSeq = 0;
/** One work row in the manual-entry form; all rows share the form's date. */
function newManualRow(clockIn = "", clockOut = "") {
  return {
    key: ++manualRowSeq,
    clockIn, clockOut, projectId: null, project: null, projectPhaseId: null, projectPhases: [],
    projectOptions: null, projectQuery: "", projectsLoading: false,
    normalHours: "", afterHours: "", overtimeHours: "", redDayHours: "",
    clockInOptional: "", clockOutOptional: "",
    comment: "",
  };
}

export default {
  name: "TimeRegistration",
  components: { Card, ProjectPickModal, NativeTimeInput, DayRegistrationEditor, DayRegistrationTab, AbsenceForm, EditEntryDialog, ElDialog },
  beforeRouteLeave() {
    return this.$refs.dayTab ? this.$refs.dayTab.confirmLeaveDay() : true;
  },
  data() {
    let activeTab = 0;
    try {
      const stored = parseInt(localStorage.getItem("timeRegistration.activeTab"), 10);
      if (stored === 0 || stored === 1) activeTab = stored;
    } catch { /* ignore */ } // eslint-disable-line no-empty
    return {
      // Tab 0: Clock widget state
      activeTab,
      currentTime: "", formattedDate: "",
      clockTimer: null, elapsedTimer: null, elapsedTime: "00:00",
      activeEntry: null, todayEntries: [], absenceTypes: [],
      showAbsenceModal: false, absenceTypeId: "", loading: false,
      absenceDateFrom: "", absenceDateTo: "", absenceHours: "", absenceFirstDayHours: "", absenceLastDayHours: "",
      breakButtonEnabled: false,

      // Project picker modal state
      showProjectPickModal: false, projectPickMode: "clockin",

      // Edit one of today's entries from the clock tab (times + project)
      entryEdit: { show: false },

      // Tab 1: Overview state
      isAdmin: false, currentUserId: null, currentView: "personal",
      selectedUserId: null, users: [], days: [],
      projectViewDays: [], projectViewProjects: [], projectViewProjectId: "",
      adjustmentsEnabled: false, // Populated from API — controls visibility of adjusted time columns
      erpTimeSyncEnabled: false, // Per-customer config — shows the "sync to ERP" button in project view
      erpDisplayName: "ERP", // Friendly name of the customer's ERP for sync labels ("Sent á DK")
      bcTimeSync: false, // ERP is BusinessCentralEndCustomers AND time sync enabled — shows the item code dropdown
      bcItems: [], // Items selected in customer settings: [{ number, displayName }]
      bcDefaultItem: "", // Item code lines bill on when no explicit choice is made
      absenceViewEnabled: false, // Per-customer config — adds the Fjarverusýn view (admins)
      absenceSyncEnabled: false, // Per-customer config — shows the "sync to ERP" button in the absence view
      erpSyncing: false,
      erpSyncResult: null,
      erpSyncEntryLabels: {},
      includeOvertimeInReport: true,
      overviewLoading: false, overviewLoaded: false,
      activeRange: "currentSalary", dateFrom: "", dateTo: "",
      customDateFrom: "", customDateTo: "", showDatePicker: false,
      salaryPeriodStartDay: 1,

      // Manual entry form
      showManualForm: false, manualSaving: false,
      manualDate: "", manualDateTo: "", manualAbsenceHours: "",
      manualType: "work", allProjects: [], manualRecentProjects: [],
      phasesEnabled: false,
      manualRows: [newManualRow("08:00", "16:00")],
      maxManualRows: 20, // matches ManualEntryBatchHandler.MaxEntries

      // Move entry to project
      allowAdminMoveTimesheetEntry: false,
      // Workers may delete and move their own past entries (AllowUsersToEditAndDeleteOwnTimesheetEntries)
      ownEntryEditsEnabled: false,
      moveModal: { show: false, entry: null, currentProjectName: "", currentProjectId: null, search: "" },

      // Simplified time registration + GPS clock-in
      simplifiedEnabled: false,
      commentsEnabled: false,
      gpsRequired: false,
      myGroup: null,
      showHoursModal: false,
      hoursForm: { normal: "", after: "", overtime: "", redDay: "" },
      hoursSaving: false,

      // Admin/user hours-edit dialog for simplified entries
      showHoursEditModal: false,
      hoursEditEntry: null,
      hoursEditForm: { normal: "", after: "", overtime: "", redDay: "", comment:"" },
      hoursEditSaving: false,

      // Admin all-users period summary report (simplified mode)
      allUsersReportActive: false,
      allUsersReportLoading: false,
      allUsersReportRows: [],

      // Excel export (admin, gated by EnableExcelExport)
      excelExportEnabled: false,
      excelExporting: false,

      // Salary period submission / approval
      periodApprovals: [],
      periodActionLoading: false,

      // "My hours this salary period" card (clock tab)
      periodSummary: { loaded: false, hours: "0.00", label: "" },

      // End-of-day registration (EnableEndOfDayTimeRegistration): tab 0 is Skrá dag; Yfirlit edits days in a dialog
      endOfDayEnabled: false,
      // EnableReferenceInTimeRegistration, only with end-of-day registration: the customer config, and the user config for own lines
      customerReferenceEnabled: false,
      referenceEnabled: false,
      dayTabKey: 0,
      dayEditor: { open: false, date: "", userId: null, changed: false },
      entryEditor: { open: false, entry: null },

      // Overtime shown from the classified pay split instead of "beyond 8h" (EnableCustomOvertimeSchedule)
      customOvertimeEnabled: false,

      // Scheduled days with nothing registered, shown in the personal view (EnableUnfinishedTimeRegistrationDaysInOverview)
      unfinishedDaysEnabled: false,
      dayPlan: [],
    };
  },

  computed: {
    todayStr() { return fmtDateStr(new Date()); },

    /** Workers can't change a submitted/approved period; admins can (the server enforces the same). */
    /** "1 færsla uppfærð, 2 færslur stofnaðar" — only the non-zero parts. */
    erpSyncResultText() {
      const r = this.erpSyncResult;
      if (!r) return "";
      const parts = [["syncToErpCreatedCount", r.created], ["syncToErpUpdatedCount", r.updated], ["syncToErpFailedCount", r.failures.length], ["syncToErpUnchangedCount", r.unchanged]]
        .filter(([, n]) => n > 0)
        .map(([key, n]) => this.$t(`timeRegistration.${key}`, { n }, n));
      return parts.join(", ");
    },
    entryEditorHeading() {
      const e = this.entryEditor.entry;
      if (!e) return "";
      const date = `${this.formatDayLabel(e.entryDate)} ${this.formatDateLarge(e.entryDate)}`;
      return e.userName ? `${e.userName} · ${date}` : date;
    },
    dayEditorLocked() {
      const a = this.periodApproval;
      if (this.isAdmin || !a || a.status == null || !this.dayEditor.date) return false;
      return this.dayEditor.date >= a.periodStart.substring(0, 10) && this.dayEditor.date <= a.periodEnd.substring(0, 10);
    },

    /** Schedule per date (yyyy-mm-dd) for the unfinished-days view. */
    dayPlanByDate() { return Object.fromEntries(this.dayPlan.map(p => [p.date.substring(0, 10), p])); },

    /** Personal view days, plus scheduled days up to today with nothing registered (EnableUnfinishedTimeRegistrationDaysInOverview). */
    personalDays() {
      if (!this.unfinishedDaysEnabled || !this.dayPlan.length) return this.days;
      const registered = new Set(this.days.map(d => String(d.date).substring(0, 10)));
      const missing = this.dayPlan
        .filter(p => {
          const date = p.date.substring(0, 10);
          return date <= this.todayStr && !p.isHoliday && !registered.has(date)
            && (Number(p.scheduledNormalHours) || 0) + (Number(p.scheduledOvertimeHours) || 0) > 0;
        })
        .map(p => ({ date: p.date, entries: [], missing: true }));
      if (!missing.length) return this.days;
      return [...this.days, ...missing].sort((a, b) => String(a.date).substring(0, 10).localeCompare(String(b.date).substring(0, 10)));
    },

    completedTodayEntries() { return this.todayEntries.filter((e) => e.isAbsence || e.realClockOut != null); },

    onBreak() { return !!this.activeEntry?.openBreakStart; },

    isSalaryPeriodRange() { return this.activeRange === "currentSalary" || this.activeRange === "lastSalary"; },

    /** Approval row for the user whose overview is shown (self for workers). */
    periodApproval() {
      const uid = this.isAdmin && this.selectedUserId ? this.selectedUserId : this.currentUserId;
      return this.periodApprovals.find((a) => a.userId === uid) || null;
    },

    hasOverviewData() {
      if (this.currentView === "project") return this.projectDays.length > 0;
      if (this.currentView === "absence") return this.absenceDays.length > 0;
      return this.days.length > 0;
    },

    showAbsenceView() {
      return this.absenceViewEnabled && this.isAdmin && !this.simplifiedEnabled;
    },

    // Absence view: all users' absence entries per day, unfiltered by project (absences have none)
    absenceDays() {
      return this.projectViewDays.map(day => {
        const workers = day.workers.map(w => {
          const entries = w.entries.filter(e => e.isAbsence);
          return entries.length ? { userId: w.userId, userName: w.userName, entries } : null;
        }).filter(Boolean);
        if (!workers.length) return null;
        return { date: day.date, workers, totalEntries: workers.reduce((s, w) => s + w.entries.length, 0) };
      }).filter(Boolean);
    },

    dateRangeLabel() {
      if (!this.dateFrom || !this.dateTo) return "";
      const f = new Date(this.dateFrom), t = new Date(this.dateTo);
      const fmtMonth = (d) => this.monthShortOf(d).toLowerCase();
      if (this.activeRange === "week") {
        const d = new Date(f); d.setDate(d.getDate() + 3);
        const wk = Math.ceil(((d - new Date(d.getFullYear(),0,4)) / 86400000 + new Date(d.getFullYear(),0,4).getDay() + 1) / 7);
        return `${this.$t("timeRegistration.weekNumber")} ${wk} · ${f.getDate()}. - ${t.getDate()}. ${fmtMonth(t)} ${t.getFullYear()}`;
      }
      if (this.activeRange === "currentSalary" || this.activeRange === "lastSalary") {
        return `${f.getDate()}. ${fmtMonth(f)} - ${t.getDate()}. ${fmtMonth(t)} ${t.getFullYear()}`;
      }
      return `${f.getDate()}. ${fmtMonth(f)} - ${t.getDate()}. ${fmtMonth(t)} ${t.getFullYear()}`;
    },

    weekRangeLabel() {
      return formatWeekRangeLabel(new Date(), this.monthShortOf);
    },

    currentSalaryPeriodLabel() {
      const p = getCurrentSalaryPeriod(new Date(), this.salaryPeriodStartDay);
      return formatSalaryPeriodLabel(p, this.monthShortOf);
    },

    lastSalaryPeriodLabel() {
      const p = getPreviousSalaryPeriod(new Date(), this.salaryPeriodStartDay);
      return formatSalaryPeriodLabel(p, this.monthShortOf);
    },

    currentWorkPeriodLabel() {
      return formatSalaryPeriodLabel(getWorkPeriod(new Date(), 0), this.monthShortOf);
    },

    lastWorkPeriodLabel() {
      return formatSalaryPeriodLabel(getWorkPeriod(new Date(), -1), this.monthShortOf);
    },

    /** Which range key the period quick buttons target — work period (calendar month) in project view, salary period otherwise. */
    currentPeriodRange() { return this.currentView === "project" ? "currentWork" : "currentSalary"; },
    /** True when the absence modal covers more than one day. */
    absenceIsRange() { return !!this.absenceDateTo && this.absenceDateTo !== this.absenceDateFrom; },
    lastPeriodRange() { return this.currentView === "project" ? "lastWork" : "lastSalary"; },

    effectiveProjectNames() {
      if (this.isAdmin) return this.projectViewProjects;
      const n = new Set();
      for (const d of this.days) for (const e of d.entries || []) if (e.projectName && !e.isAbsence) n.add(e.projectName);
      return Array.from(n).sort();
    },

    // Restructured project view: day → workers (with their entries for filtered project)
    projectDays() {
      if (this.isAdmin) return this.adminProjectDays;
      return this.userProjectDays;
    },

    userProjectDays() {
      const src = this.projectViewProjectId
        ? this.days.map(d => ({ ...d, entries: d.entries.filter(e => e.projectName === this.projectViewProjectId) })).filter(d => d.entries.length > 0)
        : this.days;
      return src.map(d => {
        // Non-admin users always see real time, never adjusted
        const { total: mins, overtime } = this.splitDayMinutes(d.entries || [], e => this.realEntryMinutes(e));
        return {
          date: d.date,
          workerCount: 1,
          totalAdjustedMinutes: mins,
          totalOvertimeMinutes: overtime,
          totalEntries: d.entries.length,
          workers: [{
            userName: this.$t("timeRegistration.myEntry"),
            entries: d.entries,
            adjustedMinutes: mins,
            overtime,
          }],
        };
      });
    },

    adminProjectDays() {
      const filter = this.projectViewProjectId;
      return this.projectViewDays.map(day => {
        const workers = day.workers.map(w => {
          const entries = filter ? w.entries.filter(e => e.projectName === filter) : w.entries;
          if (!entries.length) return null;
          const { total: adj, overtime } = this.splitDayMinutes(entries, e => e.adjustedDurationMinutes || 0);
          return { userId: w.userId, userName: w.userName, entries, adjustedMinutes: adj, overtime };
        }).filter(Boolean);

        if (!workers.length) return null;
        const totAdj = workers.reduce((s, w) => s + w.adjustedMinutes, 0);
        const totOT  = workers.reduce((s, w) => s + w.overtime, 0);
        const totE   = workers.reduce((s, w) => s + w.entries.length, 0);
        return { date: day.date, workerCount: workers.length, totalAdjustedMinutes: totAdj, totalOvertimeMinutes: totOT, totalEntries: totE, workers };
      }).filter(Boolean);
    },

    printProjectDays() {
      // Annotate each entry with normalMins and overtimeMins for the two-column breakdown.
      // Overtime is a per-worker-per-day concept: minutes beyond 480 accumulate from last entry backwards.
      return this.projectDays.map(d => {
        const workers = d.workers.map(w => {
          let remaining = 480;
          const entries = w.entries.map(e => {
            // Workers see their real time; admins the rounded billing time
            const mins = this.isAdmin && this.adjustmentsEnabled && e.adjustedDurationMinutes ? e.adjustedDurationMinutes : this.realEntryMinutes(e);
            // Entries with a pay split (hours-only, or classified with a custom overtime schedule) carry their own yfirvinna
            const splitOT = this.paySplitOvertimeMinutes(e);
            if (splitOT != null) return { ...e, _normalMins: Math.max(0, mins - splitOT), _overtimeMins: splitOT };
            const normal = Math.min(mins, remaining);
            const overtime = mins - normal;
            remaining = Math.max(0, remaining - mins);
            return { ...e, _normalMins: normal, _overtimeMins: overtime };
          });
          return { ...w, entries };
        });
        const totE = workers.reduce((s, w) => s + w.entries.length, 0);
        return { ...d, workers, totalEntries: totE };
      });
    },
    moveModalFilteredProjects() {
      const q = (this.moveModal.search || "").toLowerCase();
      const src = this.allProjects.length ? this.allProjects : [];
      if (!q) return src;
      return src.filter(p => p.name.toLowerCase().includes(q) || (p.key && p.key.toLowerCase().includes(q)));
    },

    printStats() {
      let normal = 0, overtime = 0;
      for (const d of this.projectDays) {
        for (const w of d.workers) {
          const adj = w.adjustedMinutes || w.entries.reduce((s, e) => s + (this.adjustmentsEnabled && e.adjustedDurationMinutes ? e.adjustedDurationMinutes : this.realEntryMinutes(e)), 0);
          const ot = w.overtime != null ? w.overtime : Math.max(0, adj - 480);
          normal += adj - ot;
          overtime += ot;
        }
      }
      return { normal, overtime };
    },
    manualSubmitDisabled() {
      if (this.manualSaving || !this.manualDate) return true;
      if (this.manualType !== "work") return false; // absence just needs a date + type
      if (this.simplifiedEnabled && !this.myGroup) return true;
      return this.manualRows.some(r => this.manualRowInvalid(r));
    },
    /** Sum over all manual rows: clock span (classic) or the four hour fields (simplified). */
    manualDayTotalHours() {
      return this.manualRows.reduce((sum, r) => {
        if (this.simplifiedEnabled) {
          return sum + [r.normalHours, r.afterHours, r.overtimeHours, r.redDayHours].reduce((s, v) => s + (Number(v) || 0), 0);
        }
        if (!r.clockIn || !r.clockOut) return sum;
        const [hi, mi] = r.clockIn.split(":").map(Number);
        const [ho, mo] = r.clockOut.split(":").map(Number);
        return sum + Math.max(0, ho * 60 + mo - (hi * 60 + mi)) / 60;
      }, 0);
    },
    currentStats() {
      let rM = 0, rOT = 0, aM = 0, aOT = 0;
      if (this.currentView === "project" && this.isAdmin) {
        for (const d of this.projectDays) for (const w of d.workers) {
          const dr = this.splitDayMinutes(w.entries, e => this.realEntryMinutes(e));
          rM += dr.total; rOT += dr.overtime;
          aM += w.adjustedMinutes; aOT += w.overtime;
        }
      } else {
        const src = this.currentView === "project" ? this.days.filter(d => !this.projectViewProjectId || d.entries.some(e => e.projectName === this.projectViewProjectId)) : this.days;
        for (const d of src) {
          const es = this.projectViewProjectId && this.currentView === "project" ? d.entries.filter(e => e.projectName === this.projectViewProjectId) : d.entries;
          const dr = this.splitDayMinutes(es, e => this.realEntryMinutes(e));
          rM += dr.total; rOT += dr.overtime;
          aM += d.dayMinutes || 0; aOT += d.overtimeMinutes || 0;
        }
      }
      return { realNormal: Math.max(0, rM - rOT), realOT: rOT, adjNormal: Math.max(0, aM - aOT), adjOT: aOT };
    },

    selectedUserName() {
      const u = this.users.find(x => x.id === this.selectedUserId);
      if (u && u.name) return u.name;
      for (const d of this.days) {
        for (const e of (d.entries || [])) {
          if (e.userName) return e.userName;
        }
      }
      return "";
    },

    allUsersReportTotals() {
      let n = 0, a = 0, o = 0, r = 0;
      for (const row of this.allUsersReportRows) {
        n += Number(row.normalHours) || 0;
        a += Number(row.afterHours) || 0;
        o += Number(row.overtimeHours) || 0;
        r += Number(row.redDayHours) || 0;
      }
      return { normal: n, after: a, overtime: o, redDay: r, total: n + a + o + r };
    },
  },

  async mounted() {
    const token = Cookies.get("accessToken");
    if (token) {
      const d = jwtDecode(token);
      this.isAdmin = d.role === "Admin" || d.role === "SuperAdmin";
      this.currentUserId = d.UserIdentifier || d.sub;
      this.selectedUserId = this.currentUserId;
    }
    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);

    // Load customer-level feature flags and (if simplified) the current user's group
    const [simplifiedCfg, gpsCfg, commentsCfg, salaryDayCfg, breakButtonCfg, phasesCfg, erpTimeSyncCfg, absenceViewCfg, absenceSyncCfg, endOfDayCfg, unfinishedDaysCfg, customOvertimeCfg, ownEntryEditsCfg, referenceCfg] = await Promise.all([
      getCustomerConfig("EnableSimplifiedTimeRegistration"),
      getCustomerConfig("RequireGpsClockIn"),
      getCustomerConfig("EnableCommentsOnTimesheetEntries"),
      getCustomerConfig("SalaryPeriodStartDay"),
      getCustomerConfig("EnableBreakButton"),
      getCustomerConfig("EnableProjectPhases"),
      getCustomerConfig("EnableTimeRegistrationSyncToERP"),
      getCustomerConfig("EnableAbsenceView"),
      getCustomerConfig("EnableAbsenceSyncToERP"),
      getCustomerConfig("EnableEndOfDayTimeRegistration"),
      getCustomerConfig("EnableUnfinishedTimeRegistrationDaysInOverview"),
      getCustomerConfig("EnableCustomOvertimeSchedule"),
      getCustomerConfig("AllowUsersToEditAndDeleteOwnTimesheetEntries"),
      getCustomerConfig("EnableReferenceInTimeRegistration"),
    ]);
    this.simplifiedEnabled = simplifiedCfg === true;
    this.ownEntryEditsEnabled = ownEntryEditsCfg === true;
    this.endOfDayEnabled = endOfDayCfg === true;
    this.customerReferenceEnabled = this.endOfDayEnabled && referenceCfg === true;
    if (this.customerReferenceEnabled) getUserConfig("EnableReferenceInTimeRegistration").then(v => { this.referenceEnabled = v === true; });
    this.unfinishedDaysEnabled = unfinishedDaysCfg === true;
    this.customOvertimeEnabled = customOvertimeCfg === true;
    this.phasesEnabled = phasesCfg === true;
    this.erpTimeSyncEnabled = erpTimeSyncCfg === true;
    this.absenceViewEnabled = absenceViewCfg === true;
    this.absenceSyncEnabled = absenceSyncCfg === true;
    if (this.erpTimeSyncEnabled || this.absenceSyncEnabled) {
      try {
        const customer = await loadCustomerMeCached();
        this.erpDisplayName = getErpDisplayName(customer?.erpName);
        // BC time sync: per-line item code dropdown in project view, fed by the
        // items selected in customer settings (BusinessCentralTimeRegItems config)
        if (this.erpTimeSyncEnabled && customer?.erpName === "BusinessCentralEndCustomers") {
          this.bcTimeSync = true;
          try {
            const items = await getCustomerConfig("BusinessCentralTimeRegItems");
            this.bcItems = typeof items === "string" && items ? JSON.parse(items) : [];
          } catch (err) { this.bcItems = []; } // eslint-disable-line no-unused-vars
          const defaultItem = await getCustomerConfig("BusinessCentralTimeRegDefaultItem");
          this.bcDefaultItem = defaultItem || defaultItem === 0 ? String(defaultItem) : "";
        }
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    }
    this.gpsRequired = gpsCfg === true;
    this.commentsEnabled = commentsCfg === true;
    this.breakButtonEnabled = breakButtonCfg === true;
    const sd = Number(salaryDayCfg);
    this.salaryPeriodStartDay = Number.isFinite(sd) && sd >= 1 && sd <= 31 ? Math.floor(sd) : 1;
    if (this.simplifiedEnabled) {
      try {
        const r = await api.get("/TimeRegistrationGroup/mine");
        this.myGroup = r.data || null;
      } catch (e) { this.myGroup = null; console.error(e); } // eslint-disable-line no-console
    }

    // The clock tab's today/period data isn't shown on end-of-day registration (tab 0 is Skrá dag)
    const clockData = this.endOfDayEnabled ? [] : [this.fetchTodayState(), this.fetchMyPeriodSummary()];
    const adminData = this.isAdmin ? (async () => {
      await this.fetchUsers();
      this.allowAdminMoveTimesheetEntry = await getCustomerConfig("AllowAdminMoveTimesheetEntry") === true;
      this.excelExportEnabled = await getCustomerConfig("EnableExcelExport") === true;
    })() : null;
    // Workers allowed to edit their own past entries like admins get the move-to-project action too
    const ownMoveData = !this.isAdmin && this.ownEntryEditsEnabled
      ? getCustomerConfig("AllowAdminMoveTimesheetEntry").then(v => { this.allowAdminMoveTimesheetEntry = v === true; })
      : null;
    // The personal overview doesn't need the user list, so it loads alongside it instead of after
    if (this.activeTab === 1 && !this.overviewLoaded) {
      if (this.currentView !== "personal" && adminData) await adminData;
      this.setRange(this.currentPeriodRange);
    }
    await Promise.allSettled([...clockData, this.fetchAbsenceTypes(), adminData, ownMoveData]);
  },
  beforeUnmount() { clearInterval(this.clockTimer); clearInterval(this.elapsedTimer); },

  methods: {
    /** Admins correcting another user's lines see the field whenever the customer uses it. */
    referenceEnabledFor(userId) { return userId === this.currentUserId ? this.referenceEnabled : this.customerReferenceEnabled; },
    async switchTab(i) {
      // Coming back to Skrá dag remounts it, so unsaved changes would not survive the trip
      if (this.activeTab === 0 && this.$refs.dayTab && !(await this.$refs.dayTab.confirmLeaveDay())) return;
      this.activeTab = i;
      try { localStorage.setItem("timeRegistration.activeTab", String(i)); } catch { /* ignore */ } // eslint-disable-line no-empty
      // Refresh on every switch so changes made in the other tab show right away.
      if (i === 1) {
        if (!this.overviewLoaded) this.setRange(this.currentPeriodRange);
        else this.setRange(this.activeRange);
      } else if (this.endOfDayEnabled) {
        this.dayTabKey++;
      } else {
        Promise.allSettled([this.fetchTodayState(), this.fetchMyPeriodSummary()]);
      }
    },
    toggleView() {
      this.setView(this.currentView === "personal" ? "project" : "personal");
    },
    setView(view) {
      // Simplified mode has no project concept — lock to personal view.
      if (this.simplifiedEnabled) { this.currentView = "personal"; return; }
      this.currentView = view;
      this.erpSyncResult = null; // a sync result belongs to the view it ran in
      if (view === "project") {
        if (!this.isAdmin && !this.projectViewProjectId && this.effectiveProjectNames.length) {
          this.projectViewProjectId = this.effectiveProjectNames[0];
        }
        // Project view quick filters are work periods (full calendar month) — remap an active salary range
        if (this.activeRange === "currentSalary") return this.setRange("currentWork");
        if (this.activeRange === "lastSalary") return this.setRange("lastWork");
        if (this.isAdmin && this.dateFrom) this.fetchProjectView();
      } else if (view === "absence") {
        // Absence view uses salary periods (payroll scope) and the same all-users dataset as the project view
        if (this.activeRange === "currentWork") return this.setRange("currentSalary");
        if (this.activeRange === "lastWork") return this.setRange("lastSalary");
        if (this.isAdmin && this.dateFrom) this.fetchProjectView();
      } else {
        if (this.activeRange === "currentWork") return this.setRange("currentSalary");
        if (this.activeRange === "lastWork") return this.setRange("lastSalary");
      }
    },

    // ── Clock ──────────────────────────────────────────────
    updateClock() {
      const n = new Date();
      this.currentTime = `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`;
      const dayName = weekdayName(this.$t, n);
      const monthName = this.monthShortOf(n).toLowerCase();
      this.formattedDate = `${dayName}, ${n.getDate()}. ${monthName} ${n.getFullYear()}`;
    },
    startElapsedTimer() { clearInterval(this.elapsedTimer); this.updateElapsed(); this.elapsedTimer = setInterval(this.updateElapsed, 30000); },
    updateElapsed() {
      if (!this.activeEntry?.realClockIn) return;
      const m = this.minuteDiff(this.activeEntry.realClockIn, new Date());
      this.elapsedTime = `${String(Math.floor(m / 60)).padStart(2,"0")}:${String(m % 60).padStart(2,"0")}`;
    },

    // ── Formatters ─────────────────────────────────────────
    formatTime(dt) { if (!dt) return ""; const d = new Date(dt); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; },
    formatTimeInput(dt) { return this.formatTime(dt); },
    /** Never sent, or changed after the last send (ModifiedAt later than the last sync). */
    needsErpSync(e) { return !e.erpSyncedAt || (!!e.modifiedAt && new Date(e.modifiedAt) > new Date(e.erpSyncedAt)); },
    /** ERP sync indicator: not sent / sent / changed after the last send. */
    erpSyncStatus(e) {
      if (!e.erpSyncedAt) return { cls: "text-secondary", icon: "cloud_off", text: this.$t("timeRegistration.notSyncedToErp"), title: "" };
      const sent = this.$t("timeRegistration.syncedToErp", { erp: this.erpDisplayName }) + " " + this.formatSyncTimestamp(e.erpSyncedAt);
      if (this.needsErpSync(e))
        return { cls: "text-warning", icon: "sync_problem", text: this.$t("timeRegistration.changedSinceErpSync", { erp: this.erpDisplayName }), title: sent };
      return { cls: "text-success", icon: "cloud_done", text: sent, title: "" };
    },
    formatSyncTimestamp(dt) { if (!dt) return ""; const d = new Date(dt); return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()} ${this.formatTime(d)}`; },
    // Clock times render as HH:mm, so durations must be measured between the same minute
    // boundaries the user sees — a 15:07:30 clock-in counts from 15:07, not 15:07:30.
    minuteDiff(s, e) {
      const a = new Date(s), b = new Date(e);
      a.setSeconds(0, 0); b.setSeconds(0, 0);
      return Math.max(0, Math.round((b - a) / 60000));
    },
    formatTimeSpan(s, e) { if (!s || !e) return "-"; const m = this.minuteDiff(s, e); return `${Math.floor(m/60)}:${String(m%60).padStart(2,"0")}`; },
    formatDayLabel(ds) { return weekdayShort(this.$t, new Date(ds)); },
    /** Short month name in the app's language (browsers may lack Icelandic Intl data). */
    monthShortOf(d) { return monthShort(this.$t, d); },
    formatDateLarge(ds) { const d = new Date(ds); return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}`; },
    minutesToHHMM(m) { if (!m) return "0:00"; return `${Math.floor(m/60)}:${String(m%60).padStart(2,"0")}`; },
    minutesToHM(m) { if (!m) return "0h 0m"; return `${Math.floor(m/60)}h ${m%60}m`; },
    minutesToDecimal(m) { if (!m) return "0.00"; return (m/60).toFixed(2); },
    formatHoursBreakdown(e) {
      const parts = [];
      if (e.normalHours != null) parts.push(`${Number(e.normalHours)}h V`);
      if (e.afterHours != null) parts.push(`${Number(e.afterHours)}h E`);
      if (e.overtimeHours != null) parts.push(`${Number(e.overtimeHours)}h Y`);
      if (e.redDayHours != null) parts.push(`${Number(e.redDayHours)}h S`);
      return parts.length ? parts.join(" · ") : "—";
    },
    formatDecimal(n) { if (n == null) return "0.00"; const v = Number(n); return Number.isFinite(v) ? v.toFixed(2) : "0.00"; },
    isSimplifiedEntry(e) { return e && e.timeRegistrationGroupId != null; },
    /** Sum of Vinna + Eftirvinna + Yfirvinna + Stórhátíðartímar for a single entry. Returns 0 if entry isn't simplified. */
    simplifiedEntryTotalHours(e) {
      if (!this.isSimplifiedEntry(e)) return 0;
      return (Number(e.normalHours) || 0) + (Number(e.afterHours) || 0) + (Number(e.overtimeHours) || 0) + (Number(e.redDayHours) || 0);
    },
    /** Sum hour fields across the work entries of a day. Returns {normal, after, overtime, redDay, total}. */
    dayHourTotals(day) {
      let n = 0, a = 0, o = 0, r = 0;
      for (const e of (day.entries || [])) {
        if (e.isAbsence) continue;
        if (this.isSimplifiedEntry(e)) {
          n += Number(e.normalHours) || 0;
          a += Number(e.afterHours) || 0;
          o += Number(e.overtimeHours) || 0;
          r += Number(e.redDayHours) || 0;
        }
      }
      return { normal: n, after: a, overtime: o, redDay: r, total: n + a + o + r };
    },
    /** Period totals for the Overview stats in simplified mode. Sums hour fields across all days. */
    simplifiedPeriodTotals() {
      let n = 0, a = 0, o = 0, r = 0;
      for (const d of this.days) {
        const t = this.dayHourTotals(d);
        n += t.normal; a += t.after; o += t.overtime; r += t.redDay;
      }
      return { normal: n, after: a, overtime: o, redDay: r, total: n + a + o + r };
    },
    absenceLabel(t) {
      const v = typeof t === "object" && t ? t.type : t;
      if (v === "Sickness") return this.$t("timeRegistration.sickness");
      if (v === "ChildSickness") return this.$t("timeRegistration.childSickness");
      if (v === "Vacation") return this.$t("timeRegistration.vacation");
      if (v === "PaidLeave") return this.$t("timeRegistration.paidLeave");
      if (v === "UnpaidLeave") return this.$t("timeRegistration.unpaidLeave");
      if (v === "ParentalLeave") return this.$t("timeRegistration.parentalLeave");
      return v || this.$t("timeRegistration.absenceGeneric");
    },
    /** Display label for an absence entry's hours: partial-day hours if set, otherwise the 8h full day. */
    absenceHoursLabel(e) {
      const h = e?.absenceHours != null ? Number(e.absenceHours) : 8;
      const whole = Math.floor(h);
      const mins = Math.round((h - whole) * 60);
      return `${whole}h ${mins}m`;
    },
    realEntryMinutes(e) {
      if (this.isHoursOnlyEntry(e)) return this.hoursOnlyMinutes(e);
      if (e.isAbsence || !e.realClockIn || !e.realClockOut) return 0;
      return this.minuteDiff(e.realClockIn, e.realClockOut);
    },
    /** End-of-day registration: project work with entered hours and no clock times. */
    isHoursOnlyEntry(e) { return !!e && !e.isAbsence && !e.timeRegistrationGroupId && !!(e.projectId?.value ?? e.projectId) && !e.realClockIn; },
    hoursOnlyMinutes(e) { return Math.round(((Number(e.normalHours) || 0) + (Number(e.afterHours) || 0) + (Number(e.overtimeHours) || 0) + (Number(e.redDayHours) || 0)) * 60); },
    /**
     * Overtime minutes of an entry as the pay split has them — hours-only entries always, classified
     * clock entries with a custom overtime schedule (EnableCustomOvertimeSchedule). Null = use the 8h rule.
     */
    paySplitOvertimeMinutes(e) {
      if (this.isHoursOnlyEntry(e)) return Math.round((Number(e.overtimeHours) || 0) * 60);
      if (this.customOvertimeEnabled && !this.isSimplifiedEntry(e) && e.normalHours != null) return Math.round((Number(e.overtimeHours) || 0) * 60);
      return null;
    },
    /**
     * Work and overtime minutes of one worker-day: clocked minutes beyond 8h are overtime, entries
     * with a pay split (paySplitOvertimeMinutes) count their classified/entered yfirvinna as it is.
     */
    splitDayMinutes(entries, clockMinutesOf) {
      let total = 0, ruleMinutes = 0, splitOT = 0;
      for (const e of entries) {
        if (e.isAbsence) continue;
        const mins = this.isHoursOnlyEntry(e) ? this.hoursOnlyMinutes(e) : clockMinutesOf(e);
        total += mins;
        const ot = this.paySplitOvertimeMinutes(e);
        if (ot != null) splitOT += ot;
        else ruleMinutes += mins;
      }
      return { total, overtime: Math.max(0, ruleMinutes - 480) + splitOT };
    },
    dayWorkEntries(d) { return (d.entries || []).filter(e => !e.isAbsence && e.realClockIn); },
    dayEarliestClockIn(d) {
      const work = this.dayWorkEntries(d);
      if (!work.length) return "";
      const earliest = work.reduce((min, e) => new Date(e.realClockIn) < new Date(min.realClockIn) ? e : min);
      return this.formatTimeInput(earliest.realClockIn);
    },
    dayLatestClockOut(d) {
      const work = this.dayWorkEntries(d).filter(e => e.realClockOut);
      if (!work.length) return "";
      const latest = work.reduce((max, e) => new Date(e.realClockIn) >= new Date(max.realClockIn) ? e : max);
      return this.formatTimeInput(latest.realClockOut);
    },
    async onDayClockInChange(day, val) {
      if (!val) return;
      const work = this.dayWorkEntries(day);
      if (!work.length) return;
      const earliest = work.reduce((min, e) => new Date(e.realClockIn) < new Date(min.realClockIn) ? e : min);
      await this.onTimeChange(earliest, "realClockIn", val);
    },
    async onDayClockOutChange(day, val) {
      if (!val) return;
      const work = this.dayWorkEntries(day).filter(e => e.realClockOut);
      if (!work.length) return;
      const latest = work.reduce((max, e) => new Date(e.realClockIn) >= new Date(max.realClockIn) ? e : max);
      await this.onTimeChange(latest, "realClockOut", val);
    },
    dayHasAbsence(d) { return Array.isArray(d.entries) && d.entries.some(e => e.isAbsence); },
    /** "Part of day" only for absences under 8 hours; 8 hours or no explicit hours is a full day. */
    absenceDayLabel(e) {
      const partial = e.absenceHours != null && Number(e.absenceHours) < 8;
      return partial ? this.$t("timeRegistration.partialDay") : this.$t("timeRegistration.fullDay");
    },
    /** Total absence hours for a day; entries without explicit hours count as a full 8-hour day. */
    dayAbsenceHours(d) {
      return (d.entries || [])
        .filter(e => e.isAbsence)
        .reduce((s, e) => s + (e.absenceHours != null ? Number(e.absenceHours) : 8), 0);
    },
    dayHasSimplifiedWork(d) { return Array.isArray(d.entries) && d.entries.some(e => !e.isAbsence && this.isSimplifiedEntry(e)); },
    dayHasClassicWork(d) { return Array.isArray(d.entries) && d.entries.some(e => !e.isAbsence && !this.isSimplifiedEntry(e)); },
    dayRealMinutes(d) { return (d.entries || []).reduce((s, e) => s + this.realEntryMinutes(e), 0); },
    dayRealOT(d) { return this.splitDayMinutes(d.entries || [], e => this.realEntryMinutes(e)).overtime; },
    /** Hours registered on a day: work (clocked net of deducted breaks, or entered hours) and absence. */
    dayRegisteredHours(day) {
      let work = 0, absence = 0, fullDayAbsence = false;
      for (const e of day.entries || []) {
        if (e.isAbsence) {
          if (e.absenceHours == null) fullDayAbsence = true;
          else absence += Number(e.absenceHours) || 0;
        } else if (this.isSimplifiedEntry(e) || this.isHoursOnlyEntry(e)) {
          work += (Number(e.normalHours) || 0) + (Number(e.afterHours) || 0) + (Number(e.overtimeHours) || 0) + (Number(e.redDayHours) || 0);
        } else {
          const mins = e.adjustedDurationMinutes != null ? e.adjustedDurationMinutes : this.realEntryMinutes(e);
          work += Math.max(0, mins - (e.deductedBreakMinutes || 0)) / 60;
        }
      }
      return { work, absence, fullDayAbsence };
    },
    /**
     * Hours a past/today day falls short of the schedule (dagvinna + planned yfirvinna), 0 when complete,
     * not scheduled, a holiday, or the feature is off. An absence covering the day's dagvinna completes it.
     */
    dayShortfallHours(day) {
      if (!this.unfinishedDaysEnabled) return 0;
      const date = String(day.date).substring(0, 10);
      const plan = this.dayPlanByDate[date];
      if (!plan || plan.isHoliday || date > this.todayStr) return 0;
      const normal = Number(plan.scheduledNormalHours) || 0;
      const scheduled = normal + (Number(plan.scheduledOvertimeHours) || 0);
      if (scheduled <= 0) return 0;
      const r = this.dayRegisteredHours(day);
      if (r.fullDayAbsence || (r.absence > 0 && r.absence >= normal)) return 0;
      return Math.max(0, Math.round((scheduled - r.work - r.absence) * 100) / 100);
    },
    dateStr(d) { return d.toISOString().split("T")[0]; },

    // ── Today state — loads active entry + today's completed entries for the clock widget ──
    async fetchTodayState() {
      const today = this.dateStr(new Date());
      const [a, t] = await Promise.allSettled([api.get("/Timesheet/active"), api.get(`/Timesheet?dateFrom=${today}&dateTo=${today}`)]);
      if (a.status === "fulfilled") { this.activeEntry = a.value.data || null; if (this.activeEntry && !this.activeEntry.isAbsence) this.startElapsedTimer(); }
      else if (a.reason?.response?.status !== 404) console.error("fetchActive:", a.reason); // eslint-disable-line no-console
      if (t.status === "fulfilled") { const ds = t.value.data.days || []; this.todayEntries = ds.length > 0 ? (ds[0].entries || []) : []; }
    },
    async refreshTodayEntries() {
      try { const r = await api.get(`/Timesheet?dateFrom=${this.dateStr(new Date())}&dateTo=${this.dateStr(new Date())}`); const ds = r.data.days || []; this.todayEntries = ds.length > 0 ? (ds[0].entries || []) : []; } catch (e) { console.error(e); } // eslint-disable-line no-console
    },

    // ── ProjectPickModal ───────────────────────────────────
    async openProjectModal(mode) {
      this.projectPickMode = mode;
      this.showProjectPickModal = true;
    },
    async onModalConfirm(selection) {
      const id = selection.projectId; if (!id) return;
      if (this.projectPickMode === "clockin") { this.showProjectPickModal = false; await this.clockIn(id, selection.phaseId); }
      else if (this.projectPickMode === "edit") {
        this.showProjectPickModal = false;
        Object.assign(this.entryEdit, {
          projectId: id,
          projectPhaseId: selection.phaseId || null,
          projectLabel: (selection.projectKey ? `[${selection.projectKey}] ` : "") + (selection.projectName || "") + (selection.phaseName ? ` — ${selection.phaseName}` : ""),
        });
      }
      else {
        this.showProjectPickModal = false;
        const nm = (selection.projectName || id) + (selection.phaseName ? ` — ${selection.phaseName}` : "");
        // "Skipti kl." defaults to now; an earlier time back-dates the switch ("I moved to the other job at 10:00")
        const nowHHmm = this.formatTime(new Date());
        const r = await swal.fire({ title: this.$t("timeRegistration.switchProjectTitle"), html: `${this.$t("timeRegistration.switchProjectClockOutOf")} <strong>${this.activeEntry?.projectName || this.$t("timeRegistration.currentProject")}</strong><br>${this.$t("timeRegistration.switchProjectClockInTo")} <strong>${nm}</strong>?`, icon: "question", input: "time", inputLabel: this.$t("timeRegistration.switchedAt"), inputValue: nowHHmm, inputAttributes: { lang: "en-GB" }, inputValidator: (v) => (!v || v === nowHHmm ? null : this.switchTimeError(v)), showCancelButton: true, confirmButtonText: this.$t("timeRegistration.switchProjectConfirm"), cancelButtonText: this.$t("timeRegistration.switchProjectCancel"), customClass: { confirmButton: "btn btn-success btn-fill", cancelButton: "btn btn-danger btn-fill" }, buttonsStyling: false });
        if (r.isConfirmed) await this.doProjectSwitch(id, selection.phaseId, r.value && r.value !== nowHHmm ? this.dateAtTime(new Date(), r.value) : null);
      }
    },
    /** Same rules the server enforces for a back-dated switch, as a translated message (null = valid). */
    switchTimeError(hhmm) {
      const at = this.dateAtTime(new Date(), hhmm);
      if (at > new Date()) return this.$t("timeRegistration.timeInFuture");
      if (this.activeEntry && at <= new Date(this.activeEntry.realClockIn)) return this.$t("timeRegistration.switchTimeBeforeClockIn", { time: this.formatTime(this.activeEntry.realClockIn) });
      return null;
    },
    /** The given day (Date or date string) at "HH:mm" local time. */
    dateAtTime(day, hhmm) {
      const [h, m] = hhmm.split(":").map(Number);
      const d = new Date(day); d.setHours(h, m, 0, 0);
      return d;
    },

    // ── Edit today's entries from the clock tab ────────────
    /** Today's classic work entries (running one included) are editable on the clock tab; older days stay in Yfirlit. */
    canEditTodayEntry(e) {
      return !!e && !e.isAbsence && !e.timeRegistrationGroupId && !!e.realClockIn && String(e.entryDate).slice(0, 10) === this.dateStr(new Date());
    },
    openEntryEdit(e) {
      const others = this.todayEntries.filter(o => o.id !== e.id && !o.isAbsence);
      this.entryEdit = {
        show: true, saving: false, entry: e, isOpen: !e.realClockOut,
        clockIn: this.formatTime(e.realClockIn),
        clockOut: this.formatTime(e.realClockOut),
        projectId: e.projectId?.value ?? e.projectId,
        projectPhaseId: e.projectPhaseId ?? null,
        projectLabel: (e.projectKey ? `[${e.projectKey}] ` : "") + (e.projectName || "") + (e.projectPhaseName ? ` — ${e.projectPhaseName}` : ""),
        // Back-to-back entries (e.g. from a project switch) keep their shared time in step
        hasAdjacent: others.some(o => o.realClockOut === e.realClockIn || (e.realClockOut && o.realClockIn === e.realClockOut)),
      };
    },
    async saveEntryEdit() {
      const f = this.entryEdit, e = f.entry;
      const newIn = f.clockIn && f.clockIn !== this.formatTime(e.realClockIn) ? this.dateAtTime(e.entryDate, f.clockIn) : null;
      const newOut = !f.isOpen && f.clockOut && f.clockOut !== this.formatTime(e.realClockOut) ? this.dateAtTime(e.entryDate, f.clockOut) : null;
      const projectChanged = f.projectId !== (e.projectId?.value ?? e.projectId) || f.projectPhaseId !== (e.projectPhaseId ?? null);
      if (!newIn && !newOut && !projectChanged) { f.show = false; return; }

      // Same rules the server enforces, checked here for a translated message
      const now = new Date();
      if ((newIn && newIn > now) || (newOut && newOut > now)) { this.$toast?.danger(this.$t("timeRegistration.timeInFuture"), { timeout: 3000 }); return; }
      const inAt = newIn || new Date(e.realClockIn);
      const outAt = newOut || (e.realClockOut ? new Date(e.realClockOut) : null);
      if (outAt && outAt <= inAt) { this.$toast?.danger(this.$t("timeRegistration.clockOutMustBeAfterClockIn"), { timeout: 3000 }); return; }

      const body = {};
      if (newIn) body.realClockIn = newIn.toISOString();
      if (newOut) body.realClockOut = newOut.toISOString();
      if (projectChanged) { body.projectId = f.projectId; if (f.projectPhaseId) body.projectPhaseId = f.projectPhaseId; }
      f.saving = true;
      try {
        await api.put(`/Timesheet/${e.id}`, body);
        f.show = false;
        // Neighbors may have moved with the edit, and the period total changes — reload both
        await Promise.allSettled([this.fetchTodayState(), this.fetchMyPeriodSummary()]);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.response?.data || this.$t("common.unableToUpdate");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
        console.error(err); // eslint-disable-line no-console
      } finally {
        f.saving = false;
      }
    },

    // ── Clock actions ──────────────────────────────────────
    /** Ask the browser for current coordinates. Returns null on denial/unsupported/timeout.
     *  Tries high-accuracy first, then falls back to coarse — macOS Chrome's Core Location
     *  often returns kCLErrorLocationUnknown for the high-accuracy fix before it warms up,
     *  while a wifi-derived fix is still available. */
    async getBrowserLocation() {
      if (typeof navigator === "undefined" || !navigator.geolocation) return null;
      const tryOnce = (opts) => new Promise(resolve =>
        navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), opts)
      );
      return (
        await tryOnce({ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 })
        || await tryOnce({ enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 })
      );
    },
    /** Kick off clock-in. In simplified mode, pid is ignored (group comes from /mine). */
    async startClockIn() {
      if (this.simplifiedEnabled) {
        if (!this.myGroup) {
          this.$toast?.danger(this.$t("timeRegistration.noGroupAssigned"), { timeout: 0 });
          return;
        }
        await this.clockIn(null);
      } else {
        await this.openProjectModal("clockin");
      }
    },
    async clockIn(pid, phaseId = null) {
      this.loading = true;
      try {
        const body = {};
        if (pid) body.projectId = pid;
        if (phaseId) body.projectPhaseId = phaseId;
        const pos = await this.getBrowserLocation();
        if (this.gpsRequired && !pos) {
          this.$toast?.danger(this.$t("timeRegistration.gpsRequired"), { timeout: 0 });
          return;
        }
        if (pos) {
          body.latitude = pos.coords.latitude;
          body.longitude = pos.coords.longitude;
        }
        const r = await api.post("/Timesheet/clockin", body);
        this.activeEntry = r.data;
        this.startElapsedTimer();
        await this.refreshTodayEntries();
      } catch (e) {
        const msg = e?.response?.data;
        this.$toast?.danger(msg || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.loading = false;
      }
    },
    /** For simplified entries, open the hours dialog before calling the API. Classic mode clocks out directly. */
    async clockOut() {
      if (this.activeEntry?.timeRegistrationGroupId) {
        this.hoursForm = { normal: "", after: "", overtime: "", redDay: "" };
        this.showHoursModal = true;
        return;
      }
      this.loading = true;
      try {
        const pos = await this.getBrowserLocation();
        const body = pos ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude } : {};
        await api.put("/Timesheet/clockout", body);
        this.activeEntry = null;
        clearInterval(this.elapsedTimer);
        this.elapsedTime = "00:00";
        await this.refreshTodayEntries();
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.loading = false;
      }
    },
    async submitHoursAndClockOut() {
      const allBlank = [this.hoursForm.normal, this.hoursForm.after, this.hoursForm.overtime, this.hoursForm.redDay]
        .every(v => v === "" || v == null);
      if (allBlank) {
        this.$toast?.danger(this.$t("timeRegistration.atLeastOneHourFieldRequired"), { timeout: 3000 });
        return;
      }
      const n = this.hoursForm.normal === "" || this.hoursForm.normal == null ? 0 : Number(this.hoursForm.normal);
      const a = this.hoursForm.after === "" || this.hoursForm.after == null ? 0 : Number(this.hoursForm.after);
      const o = this.hoursForm.overtime === "" || this.hoursForm.overtime == null ? 0 : Number(this.hoursForm.overtime);
      const r = this.hoursForm.redDay === "" || this.hoursForm.redDay == null ? 0 : Number(this.hoursForm.redDay);
      if ([n, a, o, r].some(v => Number.isNaN(v) || v < 0)) {
        this.$toast?.danger(this.$t("timeRegistration.hoursMustBeNonNegative"), { timeout: 3000 });
        return;
      }
      this.hoursSaving = true;
      try {
        const pos = await this.getBrowserLocation();
        const body = { normalHours: n, afterHours: a, overtimeHours: o, redDayHours: r };
        if (pos) { body.latitude = pos.coords.latitude; body.longitude = pos.coords.longitude; }
        await api.put("/Timesheet/clockout", body);
        this.activeEntry = null;
        this.showHoursModal = false;
        clearInterval(this.elapsedTimer);
        this.elapsedTime = "00:00";
        await this.refreshTodayEntries();
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.hoursSaving = false;
      }
    },
    /** switchedAt: Date when back-dating the switch; null switches now. */
    async doProjectSwitch(pid, phaseId = null, switchedAt = null) {
      this.loading = true;
      try {
        const pos = await this.getBrowserLocation();
        const body = { newProjectId: pid };
        if (phaseId) body.newProjectPhaseId = phaseId;
        if (switchedAt) body.switchedAt = switchedAt.toISOString();
        if (pos) { body.latitude = pos.coords.latitude; body.longitude = pos.coords.longitude; }
        const r = await api.put("/Timesheet/switchproject", body);
        this.activeEntry = r.data;
        this.startElapsedTimer();
        await this.refreshTodayEntries();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("common.unableToUpdate");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.loading = false; }
    },
    /** Hours worked today (adjusted duration minus deducted breaks) across today's closed work entries. */
    workedHoursToday() {
      return this.todayEntries
        .filter(e => !e.isAbsence)
        .reduce((s, e) => s + Math.max(0, (e.adjustedDurationMinutes || 0) - (e.deductedBreakMinutes || 0)), 0) / 60;
    },
    openAbsenceModal() {
      const today = this.dateStr(new Date());
      this.absenceDateFrom = today;
      this.absenceDateTo = today;
      // Prefill with what is left of the standard 8-hour day, based on time already
      // worked today. Floored to half hours so the prefill always passes validation.
      const remaining = Math.floor((8 - this.workedHoursToday()) * 2) / 2;
      this.absenceHours = remaining > 0 ? String(remaining) : "";
      // Range mode defaults (shown when the to-date is changed to another day).
      this.absenceFirstDayHours = this.absenceHours;
      this.absenceLastDayHours = "8";
      this.absenceTypeId = "";
      this.showAbsenceModal = true;
    },
    async registerAbsence() {
      const today = this.dateStr(new Date());
      const invalid = (v) => !v || Number.isNaN(Number(v)) || Number(v) <= 0 || Number(v) > 8;
      if (this.absenceIsRange) {
        if (invalid(this.absenceFirstDayHours) || invalid(this.absenceLastDayHours)) {
          this.$toast?.danger(this.$t("timeRegistration.absenceHoursInvalid"), { timeout: 3000 });
          return;
        }
        if (this.absenceDateFrom === today && Number(this.absenceFirstDayHours) + this.workedHoursToday() > 8) {
          this.$toast?.danger(this.$t("timeRegistration.absenceHoursExceedDay"), { timeout: 5000 });
          return;
        }
      } else {
        if (invalid(this.absenceHours)) {
          this.$toast?.danger(this.$t("timeRegistration.absenceHoursInvalid"), { timeout: 3000 });
          return;
        }
        if (this.absenceDateFrom === today && Number(this.absenceHours) + this.workedHoursToday() > 8) {
          this.$toast?.danger(this.$t("timeRegistration.absenceHoursExceedDay"), { timeout: 5000 });
          return;
        }
      }
      this.loading = true;
      try {
        const body = {
          dateFrom: this.absenceDateFrom,
          dateTo: this.absenceDateTo || this.absenceDateFrom,
          timesheetEntryTypeId: this.absenceTypeId,
        };
        if (this.absenceIsRange) {
          body.firstDayHours = Number(this.absenceFirstDayHours);
          body.lastDayHours = Number(this.absenceLastDayHours);
        } else {
          body.hours = Number(this.absenceHours);
        }
        const r = await api.post("/Timesheet/absence", body);
        this.showAbsenceModal = false;
        this.absenceTypeId = "";
        if (r.data?.skippedDates?.length) {
          this.$toast?.warning(`${this.$t("timeRegistration.absenceDaysSkipped")}: ${r.data.skippedDates.length}`, { timeout: 5000 });
        }
        await Promise.allSettled([this.fetchTodayState(), this.overviewLoaded ? this.fetchEntries() : Promise.resolve()]);
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("timeRegistration.registrationError"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.loading = false; }
    },
    async toggleBreak() {
      this.loading = true;
      try {
        if (this.onBreak) await api.put("/Timesheet/break/end");
        else await api.post("/Timesheet/break/start");
        const r = await api.get("/Timesheet/active");
        this.activeEntry = r.data || this.activeEntry;
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.loading = false; }
    },
    async fetchAbsenceTypes() { try { const r = await api.get("/Timesheet/types"); this.absenceTypes = (r.data || []).filter(t => t.isAbsence); } catch (e) { console.error(e); } }, // eslint-disable-line no-console

    // ── Overview — fetches data for Tab 1 (personal + project views) ─────────
    /** Loads all active users for the admin user selector dropdown. */
    async fetchUsers() {
      try {
        const r = await api.get("/User?pageSize=200&includeDisabled=false&excludeImages=true");
        const raw = r.data.result || [];
        this.users = raw.map(u => ({ id: u.id, name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email }));
        const me = this.users.find(u => u.id === this.currentUserId);
        if (me) this.users = [me, ...this.users.filter(u => u.id !== this.currentUserId)];
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    /** Sets the date range (week/currentSalary/lastSalary/currentWork/lastWork/custom) and refreshes both personal and project views. */
    setRange(range) {
      this.activeRange = range;
      const today = new Date(), ts = this.dateStr(today);
      if (range === "week") { const w = getWeekRange(today); this.dateFrom = fmtDateStr(w.from); this.dateTo = fmtDateStr(w.to); }
      else if (range === "currentSalary") {
        const p = getCurrentSalaryPeriod(today, this.salaryPeriodStartDay);
        if (!p) return;
        this.dateFrom = fmtDateStr(p.from);
        this.dateTo = p.to ? fmtDateStr(p.to) : ts;
      }
      else if (range === "lastSalary") {
        const p = getPreviousSalaryPeriod(today, this.salaryPeriodStartDay);
        if (!p) return;
        this.dateFrom = fmtDateStr(p.from);
        this.dateTo = p.to ? fmtDateStr(p.to) : ts;
      }
      else if (range === "currentWork") { const p = getWorkPeriod(today, 0); this.dateFrom = fmtDateStr(p.from); this.dateTo = fmtDateStr(p.to); }
      else if (range === "lastWork") { const p = getWorkPeriod(today, -1); this.dateFrom = fmtDateStr(p.from); this.dateTo = fmtDateStr(p.to); }
      else if (range === "custom") { if (!this.customDateFrom || !this.customDateTo) return; this.dateFrom = this.customDateFrom; this.dateTo = this.customDateTo; }
      this.fetchEntries();
      if ((this.currentView === "project" || this.currentView === "absence") && this.isAdmin) this.fetchProjectView();
      if (this.isSalaryPeriodRange) this.fetchPeriodApprovals();
    },

    // ── Salary period submission / approval ─────────────────
    async fetchPeriodApprovals() {
      if (!this.dateFrom) return;
      try {
        const r = await api.get(`/Timesheet/period/approvals?date=${this.dateFrom}`);
        this.periodApprovals = r.data || [];
      } catch (e) { this.periodApprovals = []; console.error(e); } // eslint-disable-line no-console
    },
    async submitPeriod() {
      const confirmed = await swal.fire({
        title: this.$t("timeRegistration.submitPeriodConfirmTitle"),
        text: this.$t("timeRegistration.submitPeriodConfirmText"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("timeRegistration.submitPeriod"),
        cancelButtonText: this.$t("common.cancel"),
        customClass: { confirmButton: "btn btn-success btn-fill", cancelButton: "btn btn-danger btn-fill" },
        buttonsStyling: false,
      });
      if (!confirmed.isConfirmed) return;
      this.periodActionLoading = true;
      try {
        await api.post("/Timesheet/period/submit", { date: this.dateFrom });
        await this.fetchPeriodApprovals();
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.periodActionLoading = false; }
    },
    async approvePeriod() {
      const uid = this.selectedUserId || this.currentUserId;
      this.periodActionLoading = true;
      try {
        await api.post("/Timesheet/period/approve", { date: this.dateFrom, userId: uid });
        await this.fetchPeriodApprovals();
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.periodActionLoading = false; }
    },
    async reopenPeriod() {
      const uid = this.selectedUserId || this.currentUserId;
      this.periodActionLoading = true;
      try {
        await api.post("/Timesheet/period/reopen", { date: this.dateFrom, userId: uid });
        await this.fetchPeriodApprovals();
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally { this.periodActionLoading = false; }
    },

    /** Clock tab card: the user's own net worked hours in the current salary period — real punches, never the rounded billing times. */
    async fetchMyPeriodSummary() {
      try {
        const p = getCurrentSalaryPeriod(new Date(), this.salaryPeriodStartDay);
        if (!p) return;
        const from = fmtDateStr(p.from);
        const to = p.to ? fmtDateStr(p.to) : this.dateStr(new Date());
        const r = await api.get(`/Timesheet?dateFrom=${from}&dateTo=${to}`);
        let hours = 0;
        for (const d of (r.data.days || [])) {
          for (const e of (d.entries || [])) {
            if (e.isAbsence) continue;
            if (e.timeRegistrationGroupId != null) {
              hours += (Number(e.normalHours) || 0) + (Number(e.afterHours) || 0) + (Number(e.overtimeHours) || 0) + (Number(e.redDayHours) || 0);
            } else {
              hours += Math.max(0, this.realEntryMinutes(e) - (e.deductedBreakMinutes || 0)) / 60;
            }
          }
        }
        this.periodSummary = {
          loaded: true,
          hours: hours.toFixed(2),
          label: formatSalaryPeriodLabel(p, this.monthShortOf),
        };
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    /** Fetches personal entries for the selected date range. Also reads adjustmentsEnabled from the API response. */
    async fetchEntries() {
      if (!this.dateFrom || !this.dateTo) return;
      this.overviewLoading = true;
      try {
        const up = this.isAdmin && this.selectedUserId ? `&userId=${this.selectedUserId}` : "";
        const [r, plan] = await Promise.all([
          api.get(`/Timesheet?dateFrom=${this.dateFrom}&dateTo=${this.dateTo}${up}`),
          this.fetchDayPlan(up),
        ]);
        this.days = r.data.days || []; this.adjustmentsEnabled = r.data.adjustmentsEnabled || false;
        this.dayPlan = plan;
        if (typeof r.data.simplifiedEnabled === "boolean") this.simplifiedEnabled = r.data.simplifiedEnabled;
        this.overviewLoaded = true;
      } catch (e) { console.error(e); } finally { this.overviewLoading = false; } // eslint-disable-line no-console
    },
    /** Scheduled hours per day for the unfinished-days view; the endpoint caps the range at 92 days. */
    async fetchDayPlan(userParam) {
      if (!this.unfinishedDaysEnabled) return [];
      const days = (new Date(this.dateTo) - new Date(this.dateFrom)) / 86400000;
      if (!(days >= 0 && days < 92)) return [];
      try {
        const r = await api.get(`/Timesheet/day-plan?dateFrom=${this.dateFrom}&dateTo=${this.dateTo}${userParam}`);
        return r.data || [];
      } catch (e) { console.error(e); return []; } // eslint-disable-line no-console
    },
    /** Fetches all users' entries in parallel and groups them by date → workers for the project view. */
    async syncToErp() {
      // Sync exactly what the project view is showing (respects the project filter and date range)
      const ids = [];
      const labels = {};
      const source = this.currentView === "absence" ? this.absenceDays : this.projectDays;
      for (const day of source) {
        for (const worker of day.workers) {
          for (const entry of worker.entries) {
            // Already in the ERP and unchanged since — nothing to send (the server skips these too)
            if (!this.needsErpSync(entry)) continue;
            ids.push(entry.id);
            labels[entry.id] = `${worker.userName} ${day.date ? day.date.slice(0, 10) : ""}`;
          }
        }
      }
      const uniqueIds = [...new Set(ids)];
      if (uniqueIds.length === 0) {
        const anyShown = source.some(d => d.workers.some(w => w.entries.length));
        swal.fire({ title: this.$t("timeRegistration.syncToErp", { erp: this.erpDisplayName }), text: anyShown ? this.$t("timeRegistration.syncToErpNothingChanged", { erp: this.erpDisplayName }) : this.$t("timeRegistration.syncToErpNoEntries"), icon: "info" });
        return;
      }
      const confirmed = await swal.fire({
        title: this.$t("timeRegistration.syncToErp", { erp: this.erpDisplayName }),
        text: this.$t("timeRegistration.syncToErpConfirm", { count: uniqueIds.length, erp: this.erpDisplayName }, uniqueIds.length),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("timeRegistration.syncToErp", { erp: this.erpDisplayName }),
        cancelButtonText: this.$t("common.cancel"),
        customClass: { confirmButton: "btn btn-success btn-fill", cancelButton: "btn btn-danger btn-fill" },
        buttonsStyling: false,
      });
      if (!confirmed.isConfirmed) return;

      this.erpSyncing = true;
      this.erpSyncResult = null;
      this.erpSyncEntryLabels = labels;
      try {
        const r = await api.post("/Timesheet/sync-to-erp", { timesheetEntryIds: uniqueIds });
        const lines = r.data.lines || [];
        this.erpSyncResult = {
          created: lines.filter((l) => l.statusId === 1).length,
          updated: lines.filter((l) => l.statusId === 2).length,
          failures: lines.filter((l) => l.statusId === 3),
          unchanged: r.data.unchangedCount || 0,
          errorMessage: r.data.errorMessage || null,
        };
        // Refresh so the per-entry sync indicators pick up the new sync timestamps
        this.fetchProjectView();
      } catch (e) {
        console.error("syncToErp error:", e); // eslint-disable-line no-console
        const msg = e.response && e.response.data ? JSON.stringify(e.response.data) : e.message;
        this.erpSyncResult = { created: 0, updated: 0, failures: [], errorMessage: msg };
      } finally {
        this.erpSyncing = false;
      }
    },

    async fetchProjectView() {
      if (!this.dateFrom || !this.dateTo || !this.users.length) return;
      this.overviewLoading = true;
      try {
        // One request for all users (was one per user); a failed load shows no entries, as before
        const daysByUser = {};
        try {
          const r = await api.post("/Timesheet/team-entries", { dateFrom: this.dateFrom, dateTo: this.dateTo, userIds: this.users.map(u => u.id) });
          for (const u of r.data || []) daysByUser[u.userId] = u.days || [];
        } catch (e) { console.error(e); } // eslint-disable-line no-console
        const results = this.users.map(u => ({ userId: u.id, userName: u.name, days: daysByUser[u.id] || [] }));
        const dm = {};
        for (const u of results) for (const d of u.days) {
          if (!dm[d.date]) dm[d.date] = { date: d.date, workers: [] };
          if (d.entries?.length) dm[d.date].workers.push({ userId: u.userId, userName: u.userName, entries: d.entries, dayMinutes: d.dayMinutes, overtimeMinutes: d.overtimeMinutes });
        }
        this.projectViewDays = Object.values(dm).sort((a, b) => new Date(a.date) - new Date(b.date));
        const ps = new Set();
        for (const d of this.projectViewDays) for (const w of d.workers) for (const e of w.entries) if (e.projectName) ps.add(e.projectName);
        this.projectViewProjects = Array.from(ps).sort();
        if (!this.projectViewProjectId || !this.projectViewProjects.includes(this.projectViewProjectId)) {
          this.projectViewProjectId = this.projectViewProjects[0] || "";
        }
      } catch (e) { console.error(e); } finally { this.overviewLoading = false; } // eslint-disable-line no-console
    },

    async onBcItemChange(entry, val) {
      const code = val || "";
      if (code === (entry.erpItemCode || "")) return;
      try {
        await api.put(`/Timesheet/${entry.id}`, { erpItemCode: code });
        entry.erpItemCode = code || null;
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    async onCommentChange(entry, val) {
      const comment = (val || "").trim();
      if (comment === (entry.comment || "")) return;
      try {
        const r = await api.put(`/Timesheet/${entry.id}`, { comment });
        const updated = r.data;
        for (const d of this.days) {
          const i = d.entries.findIndex(e => e.id === entry.id);
          if (i >= 0) { d.entries[i] = updated; break; }
        }
        for (const d of this.projectViewDays) {
          for (const w of d.workers) {
            const i = w.entries.findIndex(e => e.id === entry.id);
            if (i >= 0) { w.entries[i] = updated; break; }
          }
        }
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    async onTimeChange(entry, field, val) {
      if (!val) return;
      const [h, m] = val.split(":").map(Number);
      const b = new Date(entry.entryDate); b.setHours(h, m, 0, 0);
      try {
        const r = await api.put(`/Timesheet/${entry.id}`, { [field]: b.toISOString() });
        const u = r.data;
        // Update personal view
        const di = this.days.findIndex(d => new Date(d.date).toDateString() === new Date(entry.entryDate).toDateString());
        if (di >= 0) {
          const ei = this.days[di].entries.findIndex(e => e.id === entry.id);
          if (ei >= 0) {
            this.days[di].entries[ei] = u;
            const adj = this.days[di].entries.filter(e => !e.isAbsence).reduce((s, e) => s + (e.adjustedDurationMinutes || 0), 0);
            this.days[di].dayMinutes = adj; this.days[di].overtimeMinutes = Math.max(0, adj - 480);
          }
        }
        // Update project view
        for (const d of this.projectViewDays) {
          for (const w of d.workers) {
            const ei = w.entries.findIndex(e => e.id === entry.id);
            if (ei >= 0) {
              w.entries[ei] = u;
              w.adjustedMinutes = w.entries.filter(e => !e.isAbsence).reduce((s, e) => s + (e.adjustedDurationMinutes || 0), 0);
              w.overtime = Math.max(0, w.adjustedMinutes - 480);
              d.totalAdjustedMinutes = d.workers.reduce((s, wk) => s + wk.adjustedMinutes, 0);
              d.totalOvertimeMinutes = d.workers.reduce((s, wk) => s + wk.overtime, 0);
              break;
            }
          }
        }
        // A contiguous neighbor may have been stitched to the new time — refresh the range.
        await this.fetchEntries();
        if ((this.currentView === "project" || this.currentView === "absence") && this.isAdmin) this.fetchProjectView();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("common.unableToUpdate");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
        // Revert the edited field to its saved value.
        await this.fetchEntries();
        if ((this.currentView === "project" || this.currentView === "absence") && this.isAdmin) this.fetchProjectView();
        console.error(e); // eslint-disable-line no-console
      }
    },
    async deleteEntry(id) {
      if (!confirm(this.$t("timeRegistration.deleteEntryConfirm"))) return;
      try {
        await api.delete(`/Timesheet/${id}`);
        // Update personal view
        for (const d of this.days) d.entries = d.entries.filter(e => e.id !== id);
        this.days = this.days.filter(d => d.entries.length > 0);
        // Update project view
        for (const d of this.projectViewDays) {
          for (const w of d.workers) {
            w.entries = w.entries.filter(e => e.id !== id);
            w.adjustedMinutes = w.entries.filter(e => !e.isAbsence).reduce((s, e) => s + (e.adjustedDurationMinutes || 0), 0);
            w.overtime = Math.max(0, w.adjustedMinutes - 480);
          }
          d.workers = d.workers.filter(w => w.entries.length > 0);
          d.totalAdjustedMinutes = d.workers.reduce((s, wk) => s + wk.adjustedMinutes, 0);
          d.totalOvertimeMinutes = d.workers.reduce((s, wk) => s + wk.overtime, 0);
        }
        this.projectViewDays = this.projectViewDays.filter(d => d.workers.length > 0);
      } catch (e) {
        // e.g. a worker deleting inside a submitted/approved salary period
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("common.unableToUpdate");
        this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      }
    },
    /**
     * "Skrá dag" on an unfinished day: the day editor on end-of-day registration, otherwise the
     * manual-entry form pre-filled with the date (classic and simplified backfill a day there).
     */
    async registerMissingDay(date) {
      if (this.endOfDayEnabled) { this.openDayEditor(date, this.selectedUserId); return; }
      if (!this.showManualForm) await this.toggleManualForm();
      this.manualType = "work";
      this.manualDate = String(date).substring(0, 10);
      this.$nextTick(() => this.$refs.manualForm?.scrollIntoView({ behavior: "smooth", block: "center" }));
    },
    /** End-of-day registration: open the day editor for a user (admins) and date. */
    openDayEditor(date, userId) {
      this.dayEditor = { open: true, date: date ? String(date).substring(0, 10) : this.todayStr, userId: userId || this.currentUserId, changed: false };
    },
    /** Edit one end-of-day work line or absence; the overviews are reloaded so day totals follow. */
    openEntryEditor(entry) {
      this.entryEditor = { open: true, entry };
    },
    onEntryEdited() {
      this.entryEditor.open = false;
      this.fetchEntries();
      if ((this.currentView === "project" || this.currentView === "absence") && this.isAdmin) this.fetchProjectView();
    },
    onDayEditorClosed() {
      if (!this.dayEditor.changed) return;
      this.fetchEntries();
      if ((this.currentView === "project" || this.currentView === "absence") && this.isAdmin) this.fetchProjectView();
    },
    async toggleManualForm() {
      this.showManualForm = !this.showManualForm;
      // Only fetch projects for classic mode — simplified mode uses the user's group instead,
      // and end-of-day registration only registers absences here
      if (this.showManualForm && !this.simplifiedEnabled && !this.endOfDayEnabled && this.manualRecentProjects.length === 0) {
        try { this.manualRecentProjects = await this.fetchProjectOptions(20); } catch (e) { console.error(e); } // eslint-disable-line no-console
      }
    },
    onAbsenceFormSaved() {
      this.showManualForm = false;
      this.fetchEntries();
    },
    async fetchAllProjects() {
      try {
        this.allProjects = await this.fetchProjectOptions(500);
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    // Active projects, most recently used in time registration first
    async fetchProjectOptions(pageSize, searchString = "") {
      const search = searchString ? `&searchString=${encodeURIComponent(searchString)}` : "";
      const r = await api.get(`/Project?currentPage=1&pageSize=${pageSize}&projectStatusId=2&orderByLatestUsedInTimeRegistration=true${search}`);
      return (r.data.result || []).map(p => ({ id: p.id, key: p.key || "", name: p.name, phasesCount: p.phasesCount || 0, phaseRequired: p.phaseRequired === true }));
    },
    // Manual-entry project picker search (el-select debounces typing); empty query falls back to the recent list
    async searchManualProjects(row, query) {
      const q = query.trim();
      row.projectQuery = q;
      if (!q) { row.projectOptions = null; row.projectsLoading = false; return; }
      row.projectsLoading = true;
      try {
        const options = await this.fetchProjectOptions(20, q);
        if (row.projectQuery === q) row.projectOptions = options;
      } catch (e) { console.error(e); } // eslint-disable-line no-console
      finally { if (row.projectQuery === q) row.projectsLoading = false; }
    },
    // Manual-entry phase select: load the phases of the row's chosen project (when it has any)
    async onManualProjectChanged(row) {
      row.projectPhaseId = null;
      row.projectPhases = [];
      row.project = (row.projectOptions || this.manualRecentProjects).find(p => p.id === row.projectId) || null;
      if (!this.phasesEnabled || !row.project?.phasesCount) return;
      try {
        const r = await api.get(`/Project/${row.projectId}/Phases`);
        row.projectPhases = r.data || [];
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    // ERP phase-mandatory project: a phase must be chosen for the row
    // (only enforced when we actually have phases loaded for it).
    rowPhaseRequired(row) {
      if (!this.phasesEnabled || !row.projectId || row.projectPhases.length === 0) return false;
      return !!row.project?.phaseRequired;
    },
    manualRowInvalid(row) {
      if (this.simplifiedEnabled) {
        // Empty fields are treated as 0; require at least one positive value across the four.
        const vals = [row.normalHours, row.afterHours, row.overtimeHours, row.redDayHours]
          .map(v => v === "" || v == null ? 0 : Number(v));
        if (vals.some(v => Number.isNaN(v) || v < 0)) return true;
        return vals.every(v => v <= 0);
      }
      return !row.clockIn || !row.clockOut || !row.projectId
        || (this.rowPhaseRequired(row) && !row.projectPhaseId);
    },
    // A new classic row starts where the previous one ended (the usual "split the day" case).
    addManualRow() {
      const last = this.manualRows[this.manualRows.length - 1];
      this.manualRows.push(newManualRow(this.simplifiedEnabled ? "" : last?.clockOut || ""));
    },
    async submitManualEntry() {
      this.manualSaving = true;
      try {
        // When admin is viewing another user, register the entry on their behalf.
        const onBehalfOf = this.isAdmin && this.selectedUserId && this.selectedUserId !== this.currentUserId
          ? { userId: this.selectedUserId }
          : {};
        if (this.manualType === "work") {
          const dateObj = new Date(this.manualDate);
          const toIso = (hhmm) => {
            const [h, m] = hhmm.split(":").map(Number);
            const d = new Date(dateObj); d.setHours(h, m, 0, 0);
            return d.toISOString();
          };
          const comment = (r) => (this.commentsEnabled && r.comment ? { comment: r.comment } : {});
          const entries = this.manualRows.map(r => this.simplifiedEnabled
            ? {
              normalHours: Number(r.normalHours) || 0,
              afterHours: Number(r.afterHours) || 0,
              overtimeHours: Number(r.overtimeHours) || 0,
              redDayHours: Number(r.redDayHours) || 0,
              // Optional arrival/departure record — omit when unset so the server stores nulls.
              ...(r.clockInOptional ? { realClockIn: toIso(r.clockInOptional) } : {}),
              ...(r.clockOutOptional ? { realClockOut: toIso(r.clockOutOptional) } : {}),
              ...comment(r),
            }
            : {
              projectId: r.projectId,
              projectPhaseId: r.projectPhaseId,
              realClockIn: toIso(r.clockIn),
              realClockOut: toIso(r.clockOut),
              ...comment(r),
            });
          await api.post("/Timesheet/manual/batch", { date: this.manualDate, entries, ...onBehalfOf });
        } else {
          const typeId = parseInt(this.manualType.replace("absence-", ""));
          const body = {
            dateFrom: this.manualDate,
            dateTo: this.manualDateTo || this.manualDate,
            timesheetEntryTypeId: typeId,
            ...onBehalfOf,
          };
          if (this.manualAbsenceHours !== "" && this.manualAbsenceHours != null) body.hours = Number(this.manualAbsenceHours);
          const r = await api.post("/Timesheet/absence", body);
          if (r.data?.skippedDates?.length) {
            this.$toast?.warning(`${this.$t("timeRegistration.absenceDaysSkipped")}: ${r.data.skippedDates.length}`, { timeout: 5000 });
          }
        }
        this.manualDate = ""; this.manualDateTo = ""; this.manualAbsenceHours = "";
        this.manualType = "work";
        this.manualRows = [newManualRow("08:00", "16:00")];
        this.showManualForm = false;
        await this.fetchEntries();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("timeRegistration.registrationError");
        swal.fire({ title: this.$t("timeRegistration.errorTitle"), text: typeof msg === "string" ? msg : JSON.stringify(msg), icon: "error" });
      } finally { this.manualSaving = false; }
    },
    printReport() { window.print(); },

    /** Admin: fetch per-user simplified hour totals for the current period and trigger print. */
    async printAllUsersReport() {
      if (!this.dateFrom || !this.dateTo) return;
      this.allUsersReportLoading = true;
      try {
        const r = await api.get(`/Timesheet/period-summary?dateFrom=${this.dateFrom}&dateTo=${this.dateTo}`);
        this.allUsersReportRows = r.data || [];
        this.allUsersReportActive = true;
        await this.$nextTick();
        window.print();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("timeRegistration.registrationError");
        swal.fire({ title: this.$t("timeRegistration.errorTitle"), text: typeof msg === "string" ? msg : JSON.stringify(msg), icon: "error" });
      } finally {
        this.allUsersReportActive = false;
        this.allUsersReportLoading = false;
      }
    },

    /** Admin: download all users' entries for the current period as an .xlsx with a detail and a summary sheet. */
    async exportExcel() {
      if (!this.dateFrom || !this.dateTo) return;
      this.excelExporting = true;
      try {
        const r = await api.get(`/Timesheet/export?dateFrom=${this.dateFrom}&dateTo=${this.dateTo}`);
        const rows = r.data || [];

        const t = (k) => this.$t(`timeRegistration.${k}`);
        const fmtDate = (d) => { const x = new Date(d); return `${String(x.getDate()).padStart(2, "0")}.${String(x.getMonth() + 1).padStart(2, "0")}.${x.getFullYear()}`; };
        const num = (v) => (v == null ? null : Number(v));

        const detailRows = rows.map((row) => ({
          [t("employee")]: row.userName,
          [t("date")]: fmtDate(row.entryDate),
          [t("type")]: row.isAbsence ? this.absenceLabel(row.entryType) : t("workType"),
          [t("project")]: row.groupName || (row.projectKey ? `[${row.projectKey}] ${row.projectName || ""}`.trim() : row.projectName) || "",
          [t("inTime")]: this.formatTime(row.clockIn),
          [t("outTime")]: this.formatTime(row.clockOut),
          [t("workHours")]: row.workedMinutes != null ? Math.round((row.workedMinutes / 60) * 100) / 100 : null,
          [t("breakDeducted")]: num(row.deductedBreakMinutes),
          [t("normalHours")]: num(row.normalHours),
          [t("afterHours")]: num(row.afterHours),
          [t("overtimeHours")]: num(row.overtimeHours),
          [t("redDayHours")]: num(row.redDayHours),
          [t("absenceHoursLabel")]: row.isAbsence ? (num(row.absenceHours) ?? 8) : null,
          [t("autoClockedOut")]: row.wasAutoClockedOut ? "X" : "",
          [t("comment")]: row.comment || "",
        }));

        // Per-user summary: net worked hours + simplified buckets + absence day count
        const byUser = new Map();
        for (const row of rows) {
          if (!byUser.has(row.userId)) {
            byUser.set(row.userId, { name: row.userName, worked: 0, normal: 0, after: 0, overtime: 0, redDay: 0, absenceDays: 0 });
          }
          const s = byUser.get(row.userId);
          if (row.isAbsence) s.absenceDays += 1;
          else {
            s.worked += (row.workedMinutes || 0) / 60;
            s.normal += Number(row.normalHours) || 0;
            s.after += Number(row.afterHours) || 0;
            s.overtime += Number(row.overtimeHours) || 0;
            s.redDay += Number(row.redDayHours) || 0;
          }
        }
        const round2 = (v) => Math.round(v * 100) / 100;
        const summaryRows = Array.from(byUser.values()).map((s) => ({
          [t("employee")]: s.name,
          [t("workHours")]: round2(s.worked),
          [t("normalHours")]: round2(s.normal),
          [t("afterHours")]: round2(s.after),
          [t("overtimeHours")]: round2(s.overtime),
          [t("redDayHours")]: round2(s.redDay),
          [t("absenceDays")]: s.absenceDays,
        }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), t("entriesSheet").substring(0, 31));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), t("summarySheet").substring(0, 31));
        XLSX.writeFile(wb, `timesheet-${this.dateFrom}-${this.dateTo}.xlsx`);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        const msg = e?.response?.data?.detail || e?.response?.data || this.$t("common.unableToRetrieveData");
        swal.fire({ title: this.$t("timeRegistration.errorTitle"), text: typeof msg === "string" ? msg : JSON.stringify(msg), icon: "error" });
      } finally {
        this.excelExporting = false;
      }
    },

    async openMoveModal(entry) {
      if (this.allProjects.length === 0) await this.fetchAllProjects();
      this.moveModal = {
        show: true,
        entry,
        currentProjectName: (entry.projectKey ? '[' + entry.projectKey + '] ' : '') + (entry.projectName || ''),
        currentProjectId: entry.projectId?.value ?? entry.projectId,
        search: "",
        selectedProject: null,
        phases: [],
        loadingPhases: false,
      };
    },

    openHoursEditModal(entry) {
      this.hoursEditEntry = entry;
      this.hoursEditForm = {
        normal: entry.normalHours != null ? String(entry.normalHours) : "",
        after: entry.afterHours != null ? String(entry.afterHours) : "",
        overtime: entry.overtimeHours != null ? String(entry.overtimeHours) : "",
        redDay: entry.redDayHours != null ? String(entry.redDayHours) : "",
        comment: entry.comment || "",
      };
      this.showHoursEditModal = true;
    },
    async submitHoursEdit() {
      if (!this.hoursEditEntry) return;
      const n = Number(this.hoursEditForm.normal);
      const a = Number(this.hoursEditForm.after);
      const o = Number(this.hoursEditForm.overtime);
      const rd = this.hoursEditForm.redDay === "" || this.hoursEditForm.redDay == null ? 0 : Number(this.hoursEditForm.redDay);
      if ([n, a, o, rd].some(v => Number.isNaN(v) || v < 0)) {
        this.$toast?.danger(this.$t("timeRegistration.hoursMustBeNonNegative"), { timeout: 3000 });
        return;
      }
      this.hoursEditSaving = true;
      try {
        const body = { normalHours: n, afterHours: a, overtimeHours: o, redDayHours: rd };
        if (this.commentsEnabled) body.comment = this.hoursEditForm.comment || "";
        const r = await api.put(`/Timesheet/${this.hoursEditEntry.id}`, body);
        const updated = r.data;
        // Merge updates into the local `days` (personal view) and `projectViewDays` (admin group-like view).
        for (const d of this.days) {
          const i = d.entries.findIndex(e => e.id === updated.id);
          if (i >= 0) { d.entries[i] = updated; break; }
        }
        for (const d of this.projectViewDays) {
          for (const w of d.workers) {
            const i = w.entries.findIndex(e => e.id === updated.id);
            if (i >= 0) { w.entries[i] = updated; break; }
          }
        }
        this.showHoursEditModal = false;
        this.hoursEditEntry = null;
      } catch (e) {
        this.$toast?.danger(e?.response?.data || this.$t("common.unableToUpdate"), { timeout: 0 });
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.hoursEditSaving = false;
      }
    },
    async pickMoveProject(project) {
      // Projects with phases get a second step; the backend rejects phaseless moves onto phase-mandatory projects
      if (!this.phasesEnabled || !project.phasesCount) return this.confirmMove(project);
      this.moveModal.selectedProject = project;
      this.moveModal.phases = [];
      this.moveModal.loadingPhases = true;
      try {
        const r = await api.get(`/Project/${project.id?.value ?? project.id}/Phases`);
        this.moveModal.phases = r.data || [];
      } catch (e) { console.error(e); } // eslint-disable-line no-console
      this.moveModal.loadingPhases = false;
      if (!this.moveModal.phases.length) return this.confirmMove(project);
    },
    async confirmMove(project, projectPhaseId = null) {
      const entry = this.moveModal.entry;
      this.moveModal.show = false;
      try {
        const projectId = project.id?.value ?? project.id;
        const r = await api.put(`/Timesheet/${entry.id}`, { projectId, projectPhaseId, isAdmin: true });
        const updated = r.data;
        // Update personal view
        for (const d of this.days) {
          const i = d.entries.findIndex(e => e.id === entry.id);
          if (i >= 0) { d.entries[i] = updated; break; }
        }
        // Update project view
        for (const d of this.projectViewDays) {
          for (const w of d.workers) {
            const i = w.entries.findIndex(e => e.id === entry.id);
            if (i >= 0) { w.entries[i] = updated; break; }
          }
        }
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
  },
};
</script>

<style scoped>
/* ── Clock ───────────────────────────────────────────────── */
.timeclock-clock-card { background:#fff; border-radius:16px; box-shadow:0 2px 12px rgba(0,0,0,.08); display:flex; flex-direction:column; align-items:center; padding:2rem 1rem; }
.timeclock-clock-icon { width:72px; height:72px; background:#f0f0f5; border-radius:50%; display:flex; align-items:center; justify-content:center; margin-bottom:.75rem; }
.timeclock-clock-icon .material-icons { font-size:2.2rem; color:#4a4a6a; }
.timeclock-time { font-size:3rem; font-weight:700; letter-spacing:.04em; color:#1a1a2e; }
.today-entry-row { background:#f8f9fa; }
.today-entry-edit { padding:6px; margin:-6px -6px -6px 0; } /* bigger tap target on phones */

/* ── Overview header ─────────────────────────────────────── */
/* Both views use a deep, low-chroma surface so white text always clears contrast;
   the hue (indigo vs teal) is what tells personal and project apart. */
.overview-header {
  margin:-1.5rem -1.5rem 0 -1.5rem; padding:1.15rem 1.5rem 1.35rem;
  color:#fff;
}
.oh-personal { background:linear-gradient(135deg, #1e2748 0%, #2c3f6e 55%, #3d5c96 100%); }
.oh-project  { background:linear-gradient(135deg, #12333a 0%, #14565e 55%, #1b7d74 100%); }

.oh-top { display:flex; flex-wrap:wrap; align-items:flex-start; gap:.75rem; }
/* The title keeps room for itself; when the switcher doesn't fit next to it, the switcher wraps below */
.oh-titles { flex:1 1 12rem; min-width:0; }
.oh-switch { display:flex; gap:.25rem; }
.oh-switch .btn { white-space:nowrap; }
.oh-eyebrow { font-size:.68rem; letter-spacing:.09em; text-transform:uppercase; opacity:.65; }
.oh-title { font-size:1.15rem; font-weight:700; line-height:1.25; color:#fff; }

.oh-toggle {
  flex:0 0 auto; display:inline-flex; align-items:center; gap:.35rem;
  background:rgba(255,255,255,.14); border:1px solid rgba(255,255,255,.22);
  color:#fff; border-radius:999px; padding:.35rem .8rem;
  font-size:.78rem; font-weight:600; cursor:pointer; transition:background .15s;
}
.oh-toggle:hover { background:rgba(255,255,255,.24); }
.oh-toggle .material-icons { font-size:1.05rem; }

/* Scope pickers sit full-width under the title so long names are not clipped */
.header-select {
  width:100%; max-width:22rem; margin-top:.7rem;
  background:rgba(255,255,255,.14); color:#fff; border:1px solid rgba(255,255,255,.26);
}
.header-select:focus { background:rgba(255,255,255,.22); color:#fff; border-color:rgba(255,255,255,.6); box-shadow:none; }
.header-select option { color:#333; background:#fff; }

/* Stat tiles — two up on phones, four up from md for the simplified breakdown */
.oh-stats { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:.55rem; margin-top:.9rem; }
.oh-stat {
  min-width:0; padding:.6rem .7rem; border-radius:.6rem;
  background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.14);
}
.oh-stat-label {
  display:flex; align-items:flex-start; gap:.3rem; margin-bottom:.15rem;
  font-size:.66rem; font-weight:600; letter-spacing:.04em; text-transform:uppercase; opacity:.78;
  line-height:1.2;
}
.oh-stat-label .material-icons { font-size:.95rem; flex:0 0 auto; }
/* Long Icelandic compounds (Stórhátíðartímar) wrap rather than get cut off */
.oh-stat-label span { min-width:0; overflow-wrap:anywhere; }
.oh-stat-value { font-size:1.45rem; font-weight:700; line-height:1.2; white-space:nowrap; }
.oh-unit { font-size:.72rem; font-weight:500; opacity:.7; margin-left:.22rem; }
.oh-stat-sub { font-size:.7rem; opacity:.72; white-space:nowrap; margin-top:.1rem; }
.oh-stat-sub strong { font-weight:700; opacity:1; }

.oh-total {
  grid-column:1 / -1;
  display:flex; align-items:baseline; justify-content:space-between; gap:.5rem;
  padding:.5rem .7rem; border-radius:.6rem;
  background:rgba(255,255,255,.18); border:1px solid rgba(255,255,255,.2);
}
.oh-total-label { font-size:.68rem; font-weight:600; letter-spacing:.05em; text-transform:uppercase; opacity:.8; }
.oh-total-value { font-size:1.1rem; font-weight:700; white-space:nowrap; }

/* Cap the tiles on wide screens — stretched to half the viewport they read as empty boxes */
@media (min-width: 768px) {
  .oh-stats { grid-template-columns:repeat(2, minmax(0, 16rem)); }
  .oh-stats-4 { grid-template-columns:repeat(4, minmax(0, 13rem)); }
}

/* ── Day cards ───────────────────────────────────────────── */
.day-card { background:#fff; border-radius:.75rem; box-shadow:0 1px 6px rgba(0,0,0,.06); overflow:hidden; border:1px solid #eee; }
/* Date | totals on the first line, the editable arrival times on their own line below —
   keeps long Icelandic labels and the time inputs from being clipped on a phone. */
.day-card-header { display:flex; flex-wrap:wrap; align-items:flex-start; gap:.5rem; padding:1rem 1.25rem; border-bottom:1px solid #f0f0f0; }
.day-head-main { flex:1 1 auto; min-width:0; order:1; }
.day-head-totals { flex:0 0 auto; order:2; margin-left:auto; text-align:right; }
.day-total-label { font-size:.7rem; color:#888; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
.day-total-value { font-weight:700; white-space:nowrap; }
.day-arrival { flex:1 1 100%; order:3; display:flex; flex-wrap:wrap; align-items:center; gap:.4rem; }
.day-weekday { font-size:.78rem; color:#888; text-transform:uppercase; letter-spacing:.03em; }
.day-date-num { font-size:1.5rem; font-weight:700; color:#1a1a2e; line-height:1.1; }
.day-card-body { padding:.75rem 1.25rem; }
.absence-card { border-left:4px solid #ef5350; background:#fff8f8; }
.day-card-missing { border-left:4px solid #ef5350; }
.day-card-unfinished { border-left:4px solid #fb8c00; }
.day-card-unfinished .unfinished-label { color:#fb8c00; font-weight:600; font-size:.8rem; }
.day-card-unfinished.is-today { border-left-color:#adb5bd; }
.day-card-unfinished.is-today .unfinished-label { color:#8392ab; }
.day-card-missing .missing-label { color:#ef5350; font-weight:600; font-size:.875rem; }
.day-card-missing.is-today { border-left-color:#adb5bd; }
.day-card-missing.is-today .missing-label { color:#8392ab; }
.absence-row { display:flex; align-items:center; gap:.75rem; padding:.35rem 0; }
.absence-row-icon { width:36px; height:36px; border-radius:50%; background:rgba(239,83,80,.12); color:#ef5350; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.absence-row-icon .material-icons { font-size:1.2rem; }
.absence-row-hours { font-weight:700; color:#ef5350; }

/* ── Entry blocks ────────────────────────────────────────── */
.entry-block { padding:.6rem 0; }
.entry-block + .entry-block { border-top:1px solid #f0f0f0; }
.worker-block + .worker-block { border-top:1px solid #eee; padding-top:.75rem; }

/* ── Time rows ───────────────────────────────────────────── */
.time-row { display:flex; flex-wrap:wrap; align-items:flex-end; gap:.4rem; margin-top:.25rem; }
.time-label { font-size:.78rem; color:#888; width:52px; flex-shrink:0; white-space:nowrap; padding-bottom:.15rem; }
.time-sep { color:#aaa; font-size:.82rem; padding-bottom:.15rem; }
.time-dur { font-size:.78rem; color:#888; white-space:nowrap; padding-bottom:.15rem; }
.time-cell { display:flex; flex-direction:column; align-items:center; }
.time-cell s { font-size:.7rem; line-height:1.4; color:#999; }
.time-icon { font-size:1rem !important; color:#888; line-height:1.65rem; }

/* ── Simplified stats (mobile compact row) ─────────────────── */
.simplified-stats-mobile { font-size:.95rem; }
.simplified-stats-mobile span { padding:.1rem .55rem; }
.simplified-stats-mobile strong { margin-right:.25rem; }
.time-input {
  width:90px; padding:.18rem .35rem; font-size:.82rem; font-family:inherit;
  border:1px solid #d5d5d5; border-radius:5px; background:#fafafa;
  color:#333; text-align:center; transition:border-color .15s;
}
.time-input:focus { border-color:#1976d2; background:#fff; outline:none; box-shadow:0 0 0 2px rgba(25,118,210,.15); }
.time-input:disabled { background:#f5f5f5; color:#999; cursor:default; }
.time-input.adj { border-color:#b3e5fc; background:#f0faff; color:#0288d1; }

/* ── Hour breakdown chips (simplified mode) ──────────────── */
.hour-breakdown-chips { display:flex; flex-wrap:wrap; gap:.4rem; align-items:center; }
.hour-breakdown-chips .chip {
  display:inline-flex; align-items:center; gap:.25rem;
  padding:.15rem .55rem; border-radius:999px; font-size:.78rem; font-weight:500;
  border:1px solid transparent;
}
.hour-breakdown-chips .chip-normal { background:#e8f5e9; color:#2e7d32; border-color:#c8e6c9; }
.hour-breakdown-chips .chip-after { background:#fff3e0; color:#ef6c00; border-color:#ffe0b2; }
.hour-breakdown-chips .chip-overtime { background:#ffebee; color:#c62828; border-color:#ffcdd2; }
.hour-breakdown-chips .chip-redday { background:#f3e5f5; color:#6a1b9a; border-color:#e1bee7; }

/* ── Icon button ─────────────────────────────────────────── */
.btn-icon { background:none; border:none; cursor:pointer; padding:2px; line-height:1; }
.btn-icon .material-icons { font-size:1.1rem; }

/* ── Absence modal ───────────────────────────────────────── */
.timeclock-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:1rem; }
.timeclock-modal { background:#fff; border-radius:16px; width:100%; max-width:540px; padding:1.5rem; box-shadow:0 8px 40px rgba(0,0,0,.2); }

/* ── Manual entry form ───────────────────────────────────── */
.manual-form { background:#f8f9fb; border:1px solid #e8e8ee; border-radius:.6rem; padding:.85rem 1rem; }
.manual-field { display:flex; flex-direction:column; }
.manual-label { font-size:.7rem; color:#777; text-transform:uppercase; letter-spacing:.03em; margin-bottom:.2rem; font-weight:600; }
.manual-row + .manual-row { border-top:1px dashed #dcdce4; padding-top:.5rem; }
.slide-enter-active, .slide-leave-active { transition:all .2s ease; overflow:hidden; }
.slide-enter-from, .slide-leave-to { opacity:0; max-height:0; padding-top:0; padding-bottom:0; margin-top:0; }
.slide-enter-to, .slide-leave-from { opacity:1; max-height:200px; }

/* ── Date range filters ──────────────────────────────────── */
.custom-date-input { width:155px; }

/* ── Comment input ───────────────────────────────────────── */
.comment-input { font-size:.8rem; color:#555; border-color:#e0e0ea; background:#fafafa; }
.comment-input:focus { border-color:#aaa; background:#fff; }

/* ── Dark mode ───────────────────────────────────────────── */
body.dark-version .timeclock-clock-card { background:#202940; box-shadow:0 2px 12px rgba(0,0,0,.4); }
body.dark-version .timeclock-clock-icon { background:#1a2035; }
body.dark-version .timeclock-clock-icon .material-icons { color:rgba(255,255,255,.7); }
body.dark-version .timeclock-time { color:rgba(255,255,255,.9); }
body.dark-version .today-entry-row { background:#1a2035; }
body.dark-version .day-card { background:#202940; border-color:#2f3954; box-shadow:0 1px 6px rgba(0,0,0,.3); }
body.dark-version .day-card-header { border-bottom-color:rgba(255,255,255,.08); }
body.dark-version .day-weekday { color:rgba(255,255,255,.5); }
body.dark-version .day-total-label { color:rgba(255,255,255,.5); }
body.dark-version .day-date-num { color:rgba(255,255,255,.9); }
body.dark-version .absence-card { background:rgba(239,83,80,.12); }
body.dark-version .absence-row-icon { background:rgba(239,83,80,.25); }
body.dark-version .entry-block + .entry-block { border-top-color:rgba(255,255,255,.08); }
body.dark-version .worker-block + .worker-block { border-top-color:rgba(255,255,255,.08); }
body.dark-version .time-label,
body.dark-version .time-dur,
body.dark-version .time-icon { color:rgba(255,255,255,.5); }
body.dark-version .time-sep { color:rgba(255,255,255,.4); }
body.dark-version .time-cell s { color:var(--ts-text-subtle); }
body.dark-version .time-input { background:#1a2035; color:rgba(255,255,255,.85); border-color:var(--ts-border); }
body.dark-version .time-input:focus { background:#1a2035; border-color:#1976d2; }
body.dark-version .time-input:disabled { background:#1a2035; color:rgba(255,255,255,.35); }
body.dark-version .time-input.adj { border-color:rgba(2,136,209,.5); background:rgba(2,136,209,.15); color:#4fc3f7; }
body.dark-version .timeclock-modal { background:#202940; box-shadow:0 8px 40px rgba(0,0,0,.6); }
body.dark-version .manual-form { background:#1a2035; border-color:rgba(255,255,255,.08); }
body.dark-version .manual-label { color:rgba(255,255,255,.55); }
body.dark-version .manual-row + .manual-row { border-top-color:rgba(255,255,255,.12); }
body.dark-version .comment-input { background:#1a2035; color:rgba(255,255,255,.85); border-color:var(--ts-border); }
body.dark-version .comment-input:focus { background:#1a2035; border-color:var(--ts-text-muted); }

/* ── Print ───────────────────────────────────────────────── */
.print-only { display:none; }
.print-report { font-family:'Segoe UI',system-ui,sans-serif; }
.print-header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #333; padding-bottom:.75rem; margin-bottom:1rem; }
.print-header h2 { font-size:1.4rem; }
.print-table { width:100%; border-collapse:collapse; font-size:.85rem; }
.print-table th { text-align:left; padding:.4rem .6rem; border-bottom:2px solid #333; font-weight:600; }
.print-table td { padding:.35rem .6rem; border-bottom:1px solid #ddd; }
.print-table tfoot td { border-top:2px solid #333; border-bottom:none; padding-top:.5rem; }

/* ── Wider card on small/medium screens ─────────────────── */
@media (max-width: 1200px) {
  .timereg-container { padding-left:.5rem; padding-right:.5rem; }
  /* Match the row gutter to the reduced container padding, otherwise the negative
     gutter margin pushes 4px past the viewport and adds a horizontal scrollbar. */
  .timereg-container > .row { --bs-gutter-x: 1rem; }
}
@media (max-width: 768px) {
  .timereg-container { padding-left:.25rem; padding-right:.25rem; padding-top:1rem; }
  .timereg-container > .row { --bs-gutter-x: 0; }
}

/* ── Mobile UX ──────────────────────────────────────────── */
/* Reclaim the ~40px of horizontal chrome the card wrapper eats on a phone —
   that padding is what pushed the clock times and day totals out of view. */
@media (max-width: 576px) {
  .timereg-container :deep(.card-margin) { margin:12px 6px; }
  .timereg-container :deep(.card-body) { padding:1rem .85rem; }
  /* The card's default 1.5rem header padding left a big gap above the tab content */
  .timereg-container :deep(.card-header) { padding:1rem .85rem .25rem; }
  .tab-switch { display:flex; width:100%; margin-top:.6rem; }
  .tab-switch .btn { flex:1 1 0; margin-bottom:0; }
  .overview-header { margin:-1rem -.85rem 0 -.85rem; padding:1rem 1rem 1.15rem; }
  .oh-title { font-size:1.02rem; }
  .oh-stat-value { font-size:1.3rem; }
  .oh-toggle { padding:.32rem .6rem; font-size:.72rem; }
  .oh-switch { width:100%; }
  .oh-switch .btn { flex:1 1 0; min-width:0; padding:.4rem .2rem; font-size:.7rem; }
  .time-row { gap:.3rem; }
  .time-label { width:44px; font-size:.72rem; }
  .time-input { width:80px; padding:.2rem .25rem; font-size:.8rem; }
  .time-dur { font-size:.72rem; }
  .day-card-header { padding:.75rem .9rem; }
  .day-card-body { padding:.5rem .9rem; }
  .day-date-num { font-size:1.3rem; }
  .period-bar-actions { width:100%; margin-left:0 !important; }
  .period-bar-actions .btn { flex:1 1 0; }

  /* Period quick filters: even 2×2 grid instead of ragged free wrap; the
     report/export actions drop below it as full-width rows */
  .range-filters { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:.5rem; }
  .range-filter-item { align-items:stretch !important; }
  .range-filter-item .btn { width:100%; }
  .range-filter-item small { text-align:center; }
  .range-filter-actions {
    grid-column:1 / -1; margin-left:0 !important;
    flex-direction:column; align-items:stretch !important; gap:.5rem !important;
  }
  .range-filter-actions .btn { width:100%; }
  .range-filter-excel { grid-column:1 / -1; width:100%; margin-left:0 !important; }
  .custom-date-row { flex-wrap:wrap; }
  .custom-date-input { flex:1 1 0; width:auto; min-width:0; }
  .custom-date-row .btn { flex:1 1 100%; }
}

/* Very narrow phones: drop the decorative in/out arrows so the duration stays visible */
@media (max-width: 400px) {
  .oh-toggle-label { display:none; }
  .oh-toggle { padding:.35rem .45rem; }
  .oh-switch .material-icons { display:none; }
  .time-row .time-icon { display:none; }
  .day-arrival .time-icon { display:none; }
}

@media print {
  .screen-only { display:none !important; }
  .print-only { display:block !important; }
  .card { box-shadow:none !important; border:none !important; margin:0 !important; }
  .card-header { display:none !important; }
  .card-body { padding:0 !important; }
  body { font-size:11pt; }
  @page { margin:1.5cm; }
}
</style>

<!-- Global print styles (unscoped) to hide sidenav/navbar -->
<style>
@media print {
  #sidenav-main, .sidenav { display:none !important; }
  #navbarBlur, .navbar-main { display:none !important; }
  .footer { display:none !important; }
  .main-content { margin-left:0 !important; margin-right:0 !important; padding:0 !important; width:100% !important; max-width:100% !important; }
  .g-sidenav-show .main-content { margin-left:0 !important; }
  .container-fluid { padding:0 !important; }
  .py-4 { padding-top:0 !important; padding-bottom:0 !important; }
}
</style>
